import React, { useEffect, useState, useRef } from 'react';
import axios from 'axios';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeRaw from 'rehype-raw';
import {
  Sparkles, Loader2, Wand2, Trash2, Edit3, ExternalLink, RefreshCw,
  Save, X, FileText, Plus, Image as ImageIcon, Eye, Code, Type,
  Upload, Tag, User, Clock, CheckCircle, Search, Layers, PlusCircle, Check,
  Bold, Italic, Underline, Strikethrough, Heading1, Heading2, Heading3, Heading4,
  List, ListOrdered, CheckSquare, Quote, Table as TableIcon, Link as LinkIcon, Youtube,
  AlertTriangle, Lightbulb, Info, Star, HelpCircle, Columns, Maximize2, Minimize2,
  Compass, UtensilsCrossed, Zap, BookOpen, Globe,
  AlignLeft, AlignCenter, AlignRight, AlignJustify,
  Undo2, Redo2, Superscript, Subscript, Palette, Highlighter, Share2, Code2, Monitor, Settings
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { toast } from '../../hooks/use-toast';

import { API } from '../../config';
import { processMarkdownContent } from '../../utils/markdownHelper';
const tok = () => localStorage.getItem('tp_admin_token');

const DEFAULT_CATEGORIES = [
  'Travel Guide',
  'Monsoon Journeys',
  'Food on Train',
  'Station Spotlight',
  'Tips & Hacks',
  'Vande Bharat',
  'Scenic Routes',
  'Railway Heritage',
  'News & Updates',
];

const TEMPLATES = [
  {
    id: 'travel-guide',
    title: '🚆 Complete Train Travel Guide',
    icon: Compass,
    content_markdown: `## 🚄 Overview & Route Details

Welcome to our comprehensive travel guide for this iconic journey. Whether you are a daily commuter or exploring for the first time, this guide covers everything you need to know.

### 📍 Key Highlights:
- **Journey Distance:** Approx 750 km
- **Average Duration:** 8 hours 30 mins
- **Major Intermediate Stations:** Kanpur Central, Prayagraj Jn, Pt. Deen Dayal Upadhyaya Jn
- **Best Time to Travel:** October to March for pleasant weather

---

## 🎫 Classes & Seat Recommendation

| Class Code | Category | Ideal For | Catering Service |
| :--- | :--- | :--- | :--- |
| **EC** | Executive Anubhuti / Chair Car | Business Travelers & Luxury | Included (Premium Menu) |
| **CC** | AC Chair Car | Families & Solo Travelers | Available / Optional |
| **3A / 2A** | AC Sleeper Tier | Overnight Journeys | Optional |

---

> 💡 **Pro Passenger Tip:**
> Always choose window seats on the right side for breathtaking sunrise views during morning departures!

---

## 🍱 Food, Pantry & Station Dining

Railway catering offers both vegetarian and non-vegetarian meals. Additionally, e-catering via **TrainPanda** allows you to order hot restaurant food directly to your seat at major halt stations.

- **Breakfast:** Cutlets, Idli Sambhar, Upma, Tea/Coffee
- **Lunch/Dinner:** Paneer Thali, Chicken Biryani, Dal Makhani with warm rotis

---

## ❓ Frequently Asked Questions (FAQ)

### 1. Is Tatkal booking available for this route?
Yes, Tatkal booking opens at 10:00 AM for AC classes and 11:00 AM for non-AC classes one day before departure.

### 2. Are charging points available at every seat?
Yes, modern LHB and Vande Bharat coaches provide 220V sockets and USB charging ports at each seat row.
`,
    content_html: `<h2>🚄 Overview & Route Details</h2>
<p>Welcome to our comprehensive travel guide for this iconic journey. Whether you are a daily commuter or exploring for the first time, this guide covers everything you need to know.</p>

<h3>📍 Key Highlights:</h3>
<ul>
  <li><strong>Journey Distance:</strong> Approx 750 km</li>
  <li><strong>Average Duration:</strong> 8 hours 30 mins</li>
  <li><strong>Major Intermediate Stations:</strong> Kanpur Central, Prayagraj Jn, Pt. Deen Dayal Upadhyaya Jn</li>
  <li><strong>Best Time to Travel:</strong> October to March for pleasant weather</li>
</ul>

<hr style="margin:24px 0; border:0; border-top:1px solid #E2E8F0;" />

<h2>🎫 Classes & Seat Recommendation</h2>

<table style="width:100%; border-collapse:collapse; margin:20px 0;">
  <thead>
    <tr style="background:#F1F5F9;">
      <th style="padding:10px; border:1px solid #CBD5E1; text-align:left;">Class Code</th>
      <th style="padding:10px; border:1px solid #CBD5E1; text-align:left;">Category</th>
      <th style="padding:10px; border:1px solid #CBD5E1; text-align:left;">Ideal For</th>
      <th style="padding:10px; border:1px solid #CBD5E1; text-align:left;">Catering Service</th>
    </tr>
  </thead>
  <tbody>
    <tr>
      <td style="padding:10px; border:1px solid #CBD5E1;"><strong>EC</strong></td>
      <td style="padding:10px; border:1px solid #CBD5E1;">Executive Anubhuti / Chair Car</td>
      <td style="padding:10px; border:1px solid #CBD5E1;">Business Travelers & Luxury</td>
      <td style="padding:10px; border:1px solid #CBD5E1;">Included (Premium Menu)</td>
    </tr>
    <tr>
      <td style="padding:10px; border:1px solid #CBD5E1;"><strong>CC</strong></td>
      <td style="padding:10px; border:1px solid #CBD5E1;">AC Chair Car</td>
      <td style="padding:10px; border:1px solid #CBD5E1;">Families & Solo Travelers</td>
      <td style="padding:10px; border:1px solid #CBD5E1;">Available / Optional</td>
    </tr>
  </tbody>
</table>

<div style="background:#F0FDF4; border-left:4px solid #16A34A; padding:16px; border-radius:12px; margin:24px 0;">
  <strong style="color:#15803D; font-size:15px; display:block; margin-bottom:4px;">💡 Pro Passenger Tip:</strong>
  <span>Always choose window seats on the right side for breathtaking sunrise views during morning departures!</span>
</div>

<hr style="margin:24px 0; border:0; border-top:1px solid #E2E8F0;" />

<h2>🍱 Food, Pantry & Station Dining</h2>
<p>Railway catering offers both vegetarian and non-vegetarian meals. Additionally, e-catering via <strong>TrainPanda</strong> allows you to order hot restaurant food directly to your seat at major halt stations.</p>

<ul>
  <li><strong>Breakfast:</strong> Cutlets, Idli Sambhar, Upma, Tea/Coffee</li>
  <li><strong>Lunch/Dinner:</strong> Paneer Thali, Chicken Biryani, Dal Makhani with warm rotis</li>
</ul>

<hr style="margin:24px 0; border:0; border-top:1px solid #E2E8F0;" />

<h2>❓ Frequently Asked Questions (FAQ)</h2>
<p><strong>Q: Is Tatkal booking available for this route?</strong><br/><em>A: Yes, Tatkal booking opens at 10:00 AM for AC classes and 11:00 AM for non-AC classes one day before departure.</em></p>
<p><strong>Q: Are charging points available at every seat?</strong><br/><em>A: Yes, modern LHB and Vande Bharat coaches provide 220V sockets and USB charging ports at each seat row.</em></p>
`
  },
  {
    id: 'food-guide',
    title: '🍲 Station Food & Delicacies Guide',
    icon: UtensilsCrossed,
    content_markdown: `## 🍛 The Ultimate Indian Railway Food Trail

Indian train journeys are incomplete without tasting local culinary specialties served across railway stations. Here is our curated food trail.

### 🌟 Top 5 Famous Station Eats:
1. **Bhopal Junction (BPL):** Poha Jalebi & Sulemani Chai on Platform 1
2. **Nagpur Junction (NGP):** Spicy Tarri Poha & Orange Burfi
3. **Ratlam Junction (RTM):** Ratlami Sev & Garadu Chaat
4. **Howrah Junction (HWH):** Kolkata Kathi Rolls & Fresh Mishti Doi
5. **Varanasi Cantt (BSB):** Malaiyo & Banarasi Kachori Sabzi

---

> ℹ️ **How to Order on Train:**
> Enter your 10-digit PNR on TrainPanda, choose your favourite restaurant at your upcoming halt station, and pay online or Cash on Delivery.

---

## ⚖️ Pantry Car vs E-Catering Delivery

| Feature | Standard Pantry Car | TrainPanda E-Catering |
| :--- | :--- | :--- |
| **Hygiene** | Standard IRCTC Base Kitchen | FSSAI Certified Top Restaurants |
| **Variety** | Limited Standard Thali | Biryani, Pizza, North/South Indian, Jain Food |
| **Delivery** | Delivered by coach attendant | Delivered hot at station halt |
`,
    content_html: `<h2>🍛 The Ultimate Indian Railway Food Trail</h2>
<p>Indian train journeys are incomplete without tasting local culinary specialties served across railway stations. Here is our curated food trail.</p>

<h3>🌟 Top 5 Famous Station Eats:</h3>
<ol>
  <li><strong>Bhopal Junction (BPL):</strong> Poha Jalebi & Sulemani Chai on Platform 1</li>
  <li><strong>Nagpur Junction (NGP):</strong> Spicy Tarri Poha & Orange Burfi</li>
  <li><strong>Ratlam Junction (RTM):</strong> Ratlami Sev & Garadu Chaat</li>
  <li><strong>Howrah Junction (HWH):</strong> Kolkata Kathi Rolls & Fresh Mishti Doi</li>
  <li><strong>Varanasi Cantt (BSB):</strong> Malaiyo & Banarasi Kachori Sabzi</li>
</ol>

<div style="background:#EFF6FF; border-left:4px solid #2563EB; padding:16px; border-radius:12px; margin:24px 0;">
  <strong style="color:#1D4ED8; font-size:15px; display:block; margin-bottom:4px;">ℹ️ How to Order on Train:</strong>
  <span>Enter your 10-digit PNR on TrainPanda, choose your favourite restaurant at your upcoming halt station, and pay online or Cash on Delivery.</span>
</div>

<hr style="margin:24px 0; border:0; border-top:1px solid #E2E8F0;" />

<h2>⚖️ Pantry Car vs E-Catering Delivery</h2>

<table style="width:100%; border-collapse:collapse; margin:20px 0;">
  <thead>
    <tr style="background:#F1F5F9;">
      <th style="padding:10px; border:1px solid #CBD5E1; text-align:left;">Feature</th>
      <th style="padding:10px; border:1px solid #CBD5E1; text-align:left;">Standard Pantry Car</th>
      <th style="padding:10px; border:1px solid #CBD5E1; text-align:left;">TrainPanda E-Catering</th>
    </tr>
  </thead>
  <tbody>
    <tr>
      <td style="padding:10px; border:1px solid #CBD5E1;"><strong>Hygiene</strong></td>
      <td style="padding:10px; border:1px solid #CBD5E1;">Standard IRCTC Base Kitchen</td>
      <td style="padding:10px; border:1px solid #CBD5E1;">FSSAI Certified Top Restaurants</td>
    </tr>
    <tr>
      <td style="padding:10px; border:1px solid #CBD5E1;"><strong>Variety</strong></td>
      <td style="padding:10px; border:1px solid #CBD5E1;">Limited Standard Thali</td>
      <td style="padding:10px; border:1px solid #CBD5E1;">Biryani, Pizza, North/South Indian, Jain Food</td>
    </tr>
  </tbody>
</table>
`
  },
  {
    id: 'vande-bharat',
    title: '⚡ Vande Bharat Express In-Depth Review',
    icon: Zap,
    content_markdown: `## ⚡ Vande Bharat Express: Next-Gen Indian Travel

Vande Bharat Express (Train 18) has revolutionized semi-high-speed train travel in India with aerodynamic design, 160 km/h operational capability, and airplane-like interiors.

---

### 🚄 Coach Features & Amenities:
- **180° Rotating Seats** in Executive Class
- **KAVACH Anti-Collision System** for enhanced safety
- **Aerosol-based Fire Detection** & bio-vacuum toilets
- **Automatic Plug Doors** with sliding footsteps

---

> ⭐ **Speed & Timing Highlight:**
> Reduces travel time by 20% to 30% compared to standard Superfast Express trains on the same corridor.

---

## 📊 Quick Route & Fare Summary

| Parameter | Details |
| :--- | :--- |
| **Operating Days** | 6 Days a week (Except Wednesday) |
| **Average Speed** | 95-110 km/h |
| **Chair Car (CC) Fare** | ₹1,350 (Includes Meal) |
| **Executive Car (EC) Fare** | ₹2,550 (Includes Premium Meal) |
`,
    content_html: `<h2>⚡ Vande Bharat Express: Next-Gen Indian Travel</h2>
<p>Vande Bharat Express (Train 18) has revolutionized semi-high-speed train travel in India with aerodynamic design, 160 km/h operational capability, and airplane-like interiors.</p>

<hr style="margin:24px 0; border:0; border-top:1px solid #E2E8F0;" />

<h3>🚄 Coach Features & Amenities:</h3>
<ul>
  <li><strong>180° Rotating Seats</strong> in Executive Class</li>
  <li><strong>KAVACH Anti-Collision System</strong> for enhanced safety</li>
  <li><strong>Aerosol-based Fire Detection</strong> & bio-vacuum toilets</li>
  <li><strong>Automatic Plug Doors</strong> with sliding footsteps</li>
</ul>

<div style="background:#FFFBEB; border-left:4px solid #D97706; padding:16px; border-radius:12px; margin:24px 0;">
  <strong style="color:#B45309; font-size:15px; display:block; margin-bottom:4px;">⭐ Speed & Timing Highlight:</strong>
  <span>Reduces travel time by 20% to 30% compared to standard Superfast Express trains on the same corridor.</span>
</div>

<hr style="margin:24px 0; border:0; border-top:1px solid #E2E8F0;" />

<h2>📊 Quick Route & Fare Summary</h2>

<table style="width:100%; border-collapse:collapse; margin:20px 0;">
  <thead>
    <tr style="background:#F1F5F9;">
      <th style="padding:10px; border:1px solid #CBD5E1; text-align:left;">Parameter</th>
      <th style="padding:10px; border:1px solid #CBD5E1; text-align:left;">Details</th>
    </tr>
  </thead>
  <tbody>
    <tr>
      <td style="padding:10px; border:1px solid #CBD5E1;"><strong>Operating Days</strong></td>
      <td style="padding:10px; border:1px solid #CBD5E1;">6 Days a week (Except Wednesday)</td>
    </tr>
    <tr>
      <td style="padding:10px; border:1px solid #CBD5E1;"><strong>Average Speed</strong></td>
      <td style="padding:10px; border:1px solid #CBD5E1;">95-110 km/h</td>
    </tr>
    <tr>
      <td style="padding:10px; border:1px solid #CBD5E1;"><strong>Chair Car (CC) Fare</strong></td>
      <td style="padding:10px; border:1px solid #CBD5E1;">₹1,350 (Includes Meal)</td>
    </tr>
    <tr>
      <td style="padding:10px; border:1px solid #CBD5E1;"><strong>Executive Car (EC) Fare</strong></td>
      <td style="padding:10px; border:1px solid #CBD5E1;">₹2,550 (Includes Premium Meal)</td>
    </tr>
  </tbody>
</table>
`
  }
];

const INITIAL_POST = {
  title: '',
  slug: '',
  excerpt: '',
  content_markdown: '',
  content_html: '',
  content_type: 'markdown',
  hero_image: '',
  inline_images: [],
  category: 'Travel Guide',
  tags: ['Travel', 'Indian Railways'],
  meta_title: '',
  meta_description: '',
  author: 'TrainPanda Editorial',
  read_time: '4 min read',
  status: 'published',
};

const AdminStories = () => {
  const [items, setItems] = useState([]);
  const [categories, setCategories] = useState(DEFAULT_CATEGORIES);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterCat, setFilterCat] = useState('ALL');

  // Category Manager Modal state
  const [catModalOpen, setCatModalOpen] = useState(false);
  const [newCatName, setNewCatName] = useState('');
  const [savingCat, setSavingCat] = useState(false);
  const [editingCatName, setEditingCatName] = useState(null);
  const [editCatValue, setEditCatValue] = useState('');
  const [updatingCat, setUpdatingCat] = useState(false);

  // Inline category input state inside Blog Editor
  const [showInlineAddCat, setShowInlineAddCat] = useState(false);
  const [inlineCatName, setInlineCatName] = useState('');

  // AI Generator Modal state
  const [aiModalOpen, setAiModalOpen] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [manualTitle, setManualTitle] = useState('');

  // Blog Editor Modal state (used for both Create & Edit)
  const [editorOpen, setEditorOpen] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [currentId, setCurrentId] = useState(null);
  const [formData, setFormData] = useState(INITIAL_POST);
  const [editorTab, setEditorTab] = useState('editor'); // 'editor' | 'settings'
  const [viewMode, setViewMode] = useState('split'); // 'write' | 'split' | 'preview'
  const [saving, setSaving] = useState(false);
  const [uploadingImg, setUploadingImg] = useState(false);
  const [uploadingInlineImg, setUploadingInlineImg] = useState(false);
  const [tagInput, setTagInput] = useState('');

  // Link & Image dialog helper states
  // Link Modal
  const [linkModalOpen, setLinkModalOpen] = useState(false);
  const [linkText, setLinkText] = useState('');
  const [linkUrl, setLinkUrl] = useState('');
  const [linkNewTab, setLinkNewTab] = useState(true);

  // Image Modal
  const [imageModalOpen, setImageModalOpen] = useState(false);
  const [imgCaption, setImgCaption] = useState('');
  const [imgUrlInput, setImgUrlInput] = useState('');
  const [imgAlign, setImgAlign] = useState('center'); // 'left' | 'center' | 'right'
  const [imgSize, setImgSize] = useState('100%'); // '100%' | '75%' | '50%'

  // Video Modal
  const [videoModalOpen, setVideoModalOpen] = useState(false);
  const [videoUrlInput, setVideoUrlInput] = useState('');

  // Table Modal
  const [tableModalOpen, setTableModalOpen] = useState(false);
  const [tableRows, setTableRows] = useState(3);
  const [tableCols, setTableCols] = useState(3);
  const [tableHasHeader, setTableHasHeader] = useState(true);
  const [tableAlign, setTableAlign] = useState('left');

  // External Embed Modal (Map / Tweets / Widgets)
  const [embedModalOpen, setEmbedModalOpen] = useState(false);
  const [embedCodeInput, setEmbedCodeInput] = useState('');

  // Color & Highlight Popover state
  const [colorPickerOpen, setColorPickerOpen] = useState(false);

  // Fullscreen state
  const [isFullscreen, setIsFullscreen] = useState(false);

  // Undo / Redo History Stack
  const [history, setHistory] = useState([]);
  const [historyIndex, setHistoryIndex] = useState(-1);
  const isUndoRedoAction = useRef(false);

  // Autosave states
  const [lastAutosavedTime, setLastAutosavedTime] = useState(null);
  const [hasAutosavedDraft, setHasAutosavedDraft] = useState(false);
  const [autosavedDraftData, setAutosavedDraftData] = useState(null);

  const [isSlugManuallyEdited, setIsSlugManuallyEdited] = useState(false);

  const fileInputRef = useRef(null);
  const inlineFileInputRef = useRef(null);
  const textareaRef = useRef(null);
  const lastSelectionRef = useRef({ start: 0, end: 0 });

  // Check for existing autosaved draft on mount
  useEffect(() => {
    try {
      const saved = localStorage.getItem('tp_story_autosave');
      if (saved) {
        const parsed = JSON.parse(saved);
        if (parsed && (parsed.title || parsed.content_markdown || parsed.content_html)) {
          setHasAutosavedDraft(true);
          setAutosavedDraftData(parsed);
        }
      }
    } catch (_) {}
  }, []);

  // Periodic Autosave every 15 seconds when editor is open
  useEffect(() => {
    if (!editorOpen) return;
    const interval = setInterval(() => {
      if (formData.title || formData.content_markdown || formData.content_html) {
        const payload = {
          ...formData,
          autosavedAt: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
        };
        try {
          localStorage.setItem('tp_story_autosave', JSON.stringify(payload));
          setLastAutosavedTime(payload.autosavedAt);
        } catch (_) {}
      }
    }, 15000);
    return () => clearInterval(interval);
  }, [editorOpen, formData]);

  // Push to Undo/Redo history
  const pushHistory = (newFormData) => {
    if (isUndoRedoAction.current) {
      isUndoRedoAction.current = false;
      return;
    }
    const currentSnapshot = {
      content_markdown: newFormData.content_markdown || '',
      content_html: newFormData.content_html || '',
      content_type: newFormData.content_type || 'markdown',
    };
    setHistory((prev) => {
      const sliced = prev.slice(0, historyIndex + 1);
      if (sliced.length > 50) sliced.shift();
      return [...sliced, currentSnapshot];
    });
    setHistoryIndex((prev) => Math.min(prev + 1, 49));
  };

  // Undo
  const handleUndo = () => {
    if (historyIndex > 0) {
      isUndoRedoAction.current = true;
      const targetState = history[historyIndex - 1];
      setHistoryIndex(historyIndex - 1);
      setFormData((prev) => ({
        ...prev,
        content_markdown: targetState.content_markdown,
        content_html: targetState.content_html,
        content_type: targetState.content_type,
      }));
    }
  };

  // Redo
  const handleRedo = () => {
    if (historyIndex < history.length - 1) {
      isUndoRedoAction.current = true;
      const targetState = history[historyIndex + 1];
      setHistoryIndex(historyIndex + 1);
      setFormData((prev) => ({
        ...prev,
        content_markdown: targetState.content_markdown,
        content_html: targetState.content_html,
        content_type: targetState.content_type,
      }));
    }
  };

  // Global Keyboard Shortcuts (Ctrl+Z, Ctrl+Y) in Editor
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (!editorOpen) return;
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'z') {
        if (e.shiftKey) {
          e.preventDefault();
          handleRedo();
        } else {
          e.preventDefault();
          handleUndo();
        }
      } else if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'y') {
        e.preventDefault();
        handleRedo();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [editorOpen, historyIndex, history]);

  // Restore Autosaved Draft
  const restoreAutosavedDraft = () => {
    if (!autosavedDraftData) return;
    setFormData((prev) => ({
      ...prev,
      ...autosavedDraftData,
    }));
    setHasAutosavedDraft(false);
    toast({ title: 'Autosaved draft restored successfully!' });
  };

  // Clear Autosaved Draft
  const discardAutosavedDraft = () => {
    localStorage.removeItem('tp_story_autosave');
    setHasAutosavedDraft(false);
    setAutosavedDraftData(null);
    toast({ title: 'Autosaved draft cleared' });
  };

  const loadCategories = async () => {
    try {
      const r = await axios.get(`${API}/story-categories`);
      if (r.data?.categories && r.data.categories.length > 0) {
        setCategories(r.data.categories);
      }
    } catch (_) {}
  };

  const load = async () => {
    setLoading(true);
    try {
      const [rStories, rCats] = await Promise.all([
        axios.get(`${API}/admin/stories`, { headers: { Authorization: `Bearer ${tok()}` } }),
        axios.get(`${API}/story-categories`),
      ]);
      setItems(rStories.data || []);
      if (rCats.data?.categories?.length > 0) {
        setCategories(rCats.data.categories);
      }
    } catch (e) {
      toast({ title: 'Failed to load stories' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  // Add new category
  const handleAddCategory = async (catName, isFromInline = false) => {
    const trimmed = (catName || '').trim();
    if (!trimmed) {
      toast({ title: 'Please enter a category name' });
      return;
    }
    if (categories.some((c) => c.toLowerCase() === trimmed.toLowerCase())) {
      toast({ title: 'Category already exists' });
      return;
    }

    setSavingCat(true);
    try {
      const r = await axios.post(
        `${API}/admin/story-categories`,
        { name: trimmed },
        { headers: { Authorization: `Bearer ${tok()}` } }
      );
      if (r.data?.categories) {
        setCategories(r.data.categories);
      } else {
        setCategories((prev) => [...prev, trimmed]);
      }
      toast({ title: 'Category added successfully' });

      if (isFromInline) {
        setFormData((prev) => ({ ...prev, category: trimmed }));
        setInlineCatName('');
        setShowInlineAddCat(false);
      } else {
        setNewCatName('');
      }
    } catch (e) {
      toast({ title: 'Failed to add category', description: e.response?.data?.detail });
    } finally {
      setSavingCat(false);
    }
  };

  // Rename category
  const handleUpdateCategory = async (oldName) => {
    const trimmedNew = (editCatValue || '').trim();
    if (!trimmedNew) {
      toast({ title: 'Category name cannot be empty' });
      return;
    }
    if (trimmedNew.toLowerCase() === oldName.toLowerCase()) {
      setEditingCatName(null);
      return;
    }

    setUpdatingCat(true);
    try {
      const r = await axios.put(
        `${API}/admin/story-categories/${encodeURIComponent(oldName)}`,
        { new_name: trimmedNew },
        { headers: { Authorization: `Bearer ${tok()}` } }
      );
      if (r.data?.categories) {
        setCategories(r.data.categories);
      }
      toast({ title: 'Category renamed successfully' });
      setEditingCatName(null);
      setEditCatValue('');
      load();
    } catch (e) {
      toast({ title: 'Failed to rename category', description: e.response?.data?.detail });
    } finally {
      setUpdatingCat(false);
    }
  };

  // Delete category
  const handleDeleteCategory = async (catName) => {
    if (!window.confirm(`Delete category "${catName}"? Stories in this category will keep their label.`)) return;

    try {
      const r = await axios.delete(
        `${API}/admin/story-categories/${encodeURIComponent(catName)}`,
        { headers: { Authorization: `Bearer ${tok()}` } }
      );
      if (r.data?.categories) {
        setCategories(r.data.categories);
      } else {
        setCategories((prev) => prev.filter((c) => c !== catName));
      }
      toast({ title: 'Category deleted' });
      if (formData.category === catName) {
        setFormData((prev) => ({ ...prev, category: categories[0] || 'Travel Guide' }));
      }
    } catch (e) {
      toast({ title: 'Failed to delete category', description: e.response?.data?.detail });
    }
  };

  // Helper to generate clean, SEO-friendly slug
  const slugify = (text) => {
    return (text || '')
      .toString()
      .toLowerCase()
      .trim()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '') // remove accents
      .replace(/&/g, '-and-') // replace & with 'and'
      .replace(/[@#%*+=\\/|:;,?<>'"()[\]{}]/g, '-') // convert special symbols to dashes
      .replace(/[^a-z0-9\s-]/g, '') // strip non-alphanumeric
      .replace(/[\s_]+/g, '-') // convert spaces and underscores to single dash
      .replace(/-+/g, '-') // collapse consecutive dashes
      .replace(/^-+|-+$/g, '') // trim leading and trailing dashes
      .slice(0, 80);
  };

  // Open Create Dialog
  const openCreate = () => {
    setIsEditing(false);
    setCurrentId(null);
    setIsSlugManuallyEdited(false);
    setFormData(INITIAL_POST);
    setTagInput('Travel, Indian Railways');
    setEditorTab('editor');
    setViewMode('split');
    setEditorOpen(true);
  };

  // Open Edit Dialog
  const openEdit = async (id) => {
    setIsEditing(true);
    setCurrentId(id);
    setIsSlugManuallyEdited(true);
    setEditorTab('editor');
    setViewMode('split');
    setEditorOpen(true);

    const cachedItem = items.find((s) => s.id === id);
    if (cachedItem) {
      setFormData({
        title: cachedItem.title || '',
        slug: cachedItem.slug || '',
        excerpt: cachedItem.excerpt || '',
        content_markdown: cachedItem.content_markdown || '',
        content_html: cachedItem.content_html || '',
        content_type: cachedItem.content_type || 'markdown',
        hero_image: cachedItem.hero_image || '',
        inline_images: cachedItem.inline_images || [],
        category: cachedItem.category || 'Travel Guide',
        tags: cachedItem.tags || ['Travel'],
        meta_title: cachedItem.meta_title || '',
        meta_description: cachedItem.meta_description || '',
        author: cachedItem.author || 'TrainPanda Editorial',
        read_time: cachedItem.read_time || '4 min read',
        status: cachedItem.status || 'published',
      });
      setTagInput((cachedItem.tags || []).join(', '));
    }

    // Always fetch latest full story details from backend to ensure content is never lost
    try {
      const r = await axios.get(`${API}/admin/stories/${id}`, {
        headers: { Authorization: `Bearer ${tok()}` },
      });
      const item = r.data;
      if (item) {
        setFormData({
          title: item.title || '',
          slug: item.slug || '',
          excerpt: item.excerpt || '',
          content_markdown: item.content_markdown || '',
          content_html: item.content_html || '',
          content_type: item.content_type || 'markdown',
          hero_image: item.hero_image || '',
          inline_images: item.inline_images || [],
          category: item.category || 'Travel Guide',
          tags: item.tags || ['Travel'],
          meta_title: item.meta_title || '',
          meta_description: item.meta_description || '',
          author: item.author || 'TrainPanda Editorial',
          read_time: item.read_time || '4 min read',
          status: item.status || 'published',
        });
        setTagInput((item.tags || []).join(', '));
      }
    } catch (err) {
      console.error('Error fetching story:', err);
    }
  };

  // Auto-generate slug and SEO from title
  const handleTitleChange = (val) => {
    const autoSlug = slugify(val);

    setFormData((prev) => ({
      ...prev,
      title: val,
      slug: isSlugManuallyEdited ? prev.slug : autoSlug,
      meta_title: isEditing ? prev.meta_title : (val.trim() ? `${val.trim()} – TrainPanda` : ''),
      meta_description: isEditing ? prev.meta_description : (prev.meta_description || prev.excerpt || (val.trim() ? `Explore complete guide, train timings, route, food and travel tips for ${val.trim()}.` : '')),
    }));
  };

  // Custom slug change handler
  const handleSlugChange = (val) => {
    setIsSlugManuallyEdited(true);
    const sanitized = val
      .toLowerCase()
      .replace(/[^a-z0-9-]/g, '-')
      .replace(/-+/g, '-');
    setFormData((prev) => ({ ...prev, slug: sanitized }));
  };

  // 1-Click Regenerate slug from title
  const regenerateSlugFromTitle = () => {
    if (!formData.title.trim()) {
      toast({ title: 'Please enter a title first' });
      return;
    }
    const newSlug = slugify(formData.title);
    setFormData((prev) => ({ ...prev, slug: newSlug }));
    setIsSlugManuallyEdited(false);
    toast({ title: 'Slug regenerated from title!' });
  };

  // 1-Click Magic SEO generator
  const generateMagicSeo = () => {
    if (!formData.title.trim()) {
      toast({ title: 'Please enter a title first' });
      return;
    }
    const cleanTitle = formData.title.trim();
    const autoSlug = slugify(cleanTitle);

    const autoDesc = formData.excerpt?.trim() 
      ? formData.excerpt.trim().slice(0, 155)
      : `Complete travel guide for ${cleanTitle}. Check route details, train schedules, best foods, and expert passenger tips on TrainPanda.`;

    setFormData(prev => ({
      ...prev,
      slug: autoSlug,
      meta_title: `${cleanTitle} | TrainPanda Guide`,
      meta_description: autoDesc,
    }));
    toast({ title: 'SEO metadata generated successfully!' });
  };

  // Upload image to backend
  const handleImageUpload = async (e, targetField = 'hero') => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (targetField === 'hero') setUploadingImg(true);
    else setUploadingInlineImg(true);

    const body = new FormData();
    body.append('file', file);

    try {
      const r = await axios.post(`${API}/admin/upload-image`, body, {
        headers: {
          Authorization: `Bearer ${tok()}`,
          'Content-Type': 'multipart/form-data',
        },
      });
      const url = r.data?.url ? `${API.replace(/\/api$/, '')}${r.data.url}` : '';
      if (targetField === 'hero') {
        setFormData((prev) => ({ ...prev, hero_image: url }));
        toast({ title: 'Hero image uploaded!' });
      } else {
        const altText = file.name.replace(/\.[^/.]+$/, '');
        if (formData.content_type === 'html') {
          insertText(`\n<figure style="margin:20px 0; text-align:center;">\n  <img src="${url}" alt="${altText}" style="max-width:100%; border-radius:12px; box-shadow:0 4px 6px -1px rgba(0,0,0,0.1);" />\n</figure>\n`);
        } else {
          insertText(`\n![${altText}](${url})\n`);
        }
        toast({ title: 'Image inserted into editor!' });
      }
    } catch (err) {
      toast({ title: 'Upload failed', description: err.response?.data?.detail || err.message });
    } finally {
      setUploadingImg(false);
      setUploadingInlineImg(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
      if (inlineFileInputRef.current) inlineFileInputRef.current.value = '';
    }
  };

  // Cursor-aware insertion helper
  const insertText = (before, after = '', defaultPlaceholder = '') => {
    const isHtml = formData.content_type === 'html';
    const field = isHtml ? 'content_html' : 'content_markdown';
    const textarea = textareaRef.current;

    let start = lastSelectionRef.current?.start ?? (formData[field] || '').length;
    let end = lastSelectionRef.current?.end ?? start;
    if (textarea && typeof textarea.selectionStart === 'number') {
      start = textarea.selectionStart;
      end = textarea.selectionEnd;
    }

    const currentVal = formData[field] || '';
    if (start > currentVal.length) start = currentVal.length;
    if (end > currentVal.length) end = currentVal.length;
    if (start > end) {
      const tmp = start;
      start = end;
      end = tmp;
    }

    const selectedText = currentVal.substring(start, end) || defaultPlaceholder;
    const replacement = before + selectedText + after;
    const updated = currentVal.substring(0, start) + replacement + currentVal.substring(end);

    const nextForm = { ...formData, [field]: updated };
    setFormData(nextForm);
    pushHistory(nextForm);

    setTimeout(() => {
      if (textarea) {
        textarea.focus();
        const cursorTarget = start + before.length + selectedText.length;
        textarea.setSelectionRange(cursorTarget, cursorTarget);
        lastSelectionRef.current = { start: cursorTarget, end: cursorTarget };
      }
    }, 0);
  };

  // Drag & Drop Image Handler for Textarea
  const handleDropImage = async (e) => {
    e.preventDefault();
    const files = e.dataTransfer?.files;
    if (!files || files.length === 0) return;

    const file = files[0];
    if (!file.type.startsWith('image/')) {
      toast({ title: 'Please drop an image file' });
      return;
    }

    setUploadingInlineImg(true);
    const body = new FormData();
    body.append('file', file);

    try {
      const r = await axios.post(`${API}/admin/upload-image`, body, {
        headers: {
          Authorization: `Bearer ${tok()}`,
          'Content-Type': 'multipart/form-data',
        },
      });
      const url = r.data?.url ? `${API.replace(/\/api$/, '')}${r.data.url}` : '';
      const altText = file.name.replace(/\.[^/.]+$/, '');
      if (formData.content_type === 'html') {
        insertText(`\n<figure style="margin:20px 0; text-align:center;">\n  <img src="${url}" alt="${altText}" style="max-width:100%; border-radius:12px; box-shadow:0 4px 6px -1px rgba(0,0,0,0.1);" />\n  <figcaption style="font-size:12px; color:#6B7280; margin-top:6px;">${altText}</figcaption>\n</figure>\n`);
      } else {
        insertText(`\n![${altText}](${url})\n`);
      }
      toast({ title: 'Image dropped & uploaded successfully!' });
    } catch (err) {
      toast({ title: 'Image drop upload failed', description: err.message });
    } finally {
      setUploadingInlineImg(false);
    }
  };

  // Paste Sanitizer (Clean Word / Google Docs formatting)
  const handlePasteSanitizer = (e) => {
    const html = e.clipboardData.getData('text/html');
    if (html && (html.includes('MsoNormal') || html.includes('docs-internal-guid') || html.includes('mso-'))) {
      e.preventDefault();
      // Extract clean plain text with preserved line breaks
      const text = e.clipboardData.getData('text/plain');
      insertText(text);
      toast({ title: 'Pasted with clean formatting from document' });
    }
  };

  // Alignment Helper
  const applyAlignment = (align) => {
    if (formData.content_type === 'html') {
      insertText(`<div style="text-align:${align};">\n  `, '\n</div>\n', 'Aligned text');
    } else {
      insertText(`[${align}]`, `[/${align}]`, 'Aligned text');
    }
  };

  // Text Color Helper
  const applyTextColor = (colorCode) => {
    if (formData.content_type === 'html') {
      insertText(`<span style="color:${colorCode};">`, '</span>', 'Colored text');
    } else {
      insertText(`{color:${colorCode}}`, '{/color}', 'Colored text');
    }
    setColorPickerOpen(false);
  };

  // Highlight Background Helper
  const applyHighlight = (bgCode) => {
    if (formData.content_type === 'html') {
      insertText(`<mark style="background-color:${bgCode}; padding:2px 6px; border-radius:4px; color:#1E293B;">`, '</mark>', 'Highlighted text');
    } else {
      insertText(`{bg:${bgCode}}`, '{/bg}', 'Highlighted text');
    }
    setColorPickerOpen(false);
  };

  // Apply Heading Level 1-4 (Robust for both HTML & Markdown)
  const applyHeading = (level = 2) => {
    const isHtml = formData.content_type === 'html';
    const field = isHtml ? 'content_html' : 'content_markdown';
    const currentVal = formData[field] || '';
    const textarea = textareaRef.current;

    let start = lastSelectionRef.current?.start ?? currentVal.length;
    let end = lastSelectionRef.current?.end ?? start;
    if (textarea && typeof textarea.selectionStart === 'number') {
      start = textarea.selectionStart;
      end = textarea.selectionEnd;
    }
    if (start > currentVal.length) start = currentVal.length;
    if (end > currentVal.length) end = currentVal.length;
    if (start > end) {
      const tmp = start;
      start = end;
      end = tmp;
    }

    // Case 1: Text highlighted
    if (start < end) {
      const selected = currentVal.substring(start, end);
      let headingText = selected.trim();

      let replacement = '';
      if (isHtml) {
        headingText = headingText.replace(/^<h[1-6][^>]*>(.*?)<\/h[1-6]>$/is, '$1')
                                 .replace(/^<p[^>]*>(.*?)<\/p>$/is, '$1').trim();
        if (!headingText) headingText = `Heading ${level}`;
        const prefix = (start > 0 && currentVal[start - 1] !== '\n') ? '\n' : '';
        const suffix = (end < currentVal.length && currentVal[end] !== '\n') ? '\n' : '';
        replacement = `${prefix}<h${level}>${headingText}</h${level}>${suffix}`;
      } else {
        headingText = headingText.replace(/^#+\s*/, '').trim();
        if (!headingText) headingText = `Heading ${level}`;
        const prefix = (start > 0 && currentVal[start - 1] !== '\n') ? '\n' : '';
        const hashes = '#'.repeat(level);
        const suffix = (end < currentVal.length && currentVal[end] !== '\n') ? '\n' : '\n';
        replacement = `${prefix}${hashes} ${headingText}${suffix}`;
      }

      const updated = currentVal.substring(0, start) + replacement + currentVal.substring(end);
      const nextForm = { ...formData, [field]: updated };
      setFormData(nextForm);
      pushHistory(nextForm);

      setTimeout(() => {
        if (textarea) {
          textarea.focus();
          const newPos = start + replacement.length;
          textarea.setSelectionRange(newPos, newPos);
          lastSelectionRef.current = { start: newPos, end: newPos };
        }
      }, 0);
      return;
    }

    // Case 2: Cursor on a line with text
    const lastNewlineBefore = currentVal.lastIndexOf('\n', start - 1);
    const lineStart = lastNewlineBefore === -1 ? 0 : lastNewlineBefore + 1;
    const nextNewlineAfter = currentVal.indexOf('\n', start);
    const lineEnd = nextNewlineAfter === -1 ? currentVal.length : nextNewlineAfter;
    const currentLine = currentVal.substring(lineStart, lineEnd);

    if (currentLine.trim().length > 0) {
      let cleanText = currentLine.trim();
      let replacement = '';
      if (isHtml) {
        cleanText = cleanText.replace(/^<h[1-6][^>]*>(.*?)<\/h[1-6]>$/is, '$1')
                             .replace(/^<p[^>]*>(.*?)<\/p>$/is, '$1').trim();
        replacement = `<h${level}>${cleanText}</h${level}>`;
      } else {
        cleanText = cleanText.replace(/^#+\s*/, '').trim();
        const hashes = '#'.repeat(level);
        replacement = `${hashes} ${cleanText}`;
      }

      const updated = currentVal.substring(0, lineStart) + replacement + currentVal.substring(lineEnd);
      const nextForm = { ...formData, [field]: updated };
      setFormData(nextForm);
      pushHistory(nextForm);

      setTimeout(() => {
        if (textarea) {
          textarea.focus();
          const newPos = lineStart + replacement.length;
          textarea.setSelectionRange(newPos, newPos);
          lastSelectionRef.current = { start: newPos, end: newPos };
        }
      }, 0);
    } else {
      // Case 3: Empty line
      const placeholder = `Heading ${level}`;
      let replacement = '';
      let selStartOffset = 0;
      let selEndOffset = 0;

      if (isHtml) {
        const prefix = (start > 0 && currentVal[start - 1] !== '\n') ? '\n' : '';
        replacement = `${prefix}<h${level}>${placeholder}</h${level}>\n`;
        selStartOffset = prefix.length + `<h${level}>`.length;
        selEndOffset = selStartOffset + placeholder.length;
      } else {
        const prefix = (start > 0 && currentVal[start - 1] !== '\n') ? '\n' : '';
        const hashes = '#'.repeat(level);
        replacement = `${prefix}${hashes} ${placeholder}\n`;
        selStartOffset = prefix.length + hashes.length + 1;
        selEndOffset = selStartOffset + placeholder.length;
      }

      const updated = currentVal.substring(0, start) + replacement + currentVal.substring(end);
      const nextForm = { ...formData, [field]: updated };
      setFormData(nextForm);
      pushHistory(nextForm);

      setTimeout(() => {
        if (textarea) {
          textarea.focus();
          const s = start + selStartOffset;
          const e = start + selEndOffset;
          textarea.setSelectionRange(s, e);
          lastSelectionRef.current = { start: s, end: e };
        }
      }, 0);
    }
  };

  // Open Link Modal (Pre-populate with highlighted text)
  const handleOpenLinkModal = () => {
    const isHtml = formData.content_type === 'html';
    const field = isHtml ? 'content_html' : 'content_markdown';
    const currentVal = formData[field] || '';
    const textarea = textareaRef.current;

    let start = lastSelectionRef.current?.start ?? 0;
    let end = lastSelectionRef.current?.end ?? 0;
    if (textarea && typeof textarea.selectionStart === 'number') {
      start = textarea.selectionStart;
      end = textarea.selectionEnd;
    }
    lastSelectionRef.current = { start, end };

    const selected = (start < end) ? currentVal.substring(start, end) : '';
    if (selected.trim()) {
      if (selected.trim().startsWith('http://') || selected.trim().startsWith('https://') || selected.trim().startsWith('/')) {
        setLinkUrl(selected.trim());
        setLinkText('');
      } else {
        setLinkText(selected.trim());
        setLinkUrl('');
      }
    } else {
      setLinkText('');
      setLinkUrl('');
    }
    setLinkModalOpen(true);
  };

  // Insert Link Modal Submit
  const handleInsertLinkSubmit = () => {
    if (!linkUrl.trim()) {
      toast({ title: 'Please enter a URL' });
      return;
    }
    const isHtml = formData.content_type === 'html';
    const field = isHtml ? 'content_html' : 'content_markdown';
    const currentVal = formData[field] || '';
    const textarea = textareaRef.current;

    let start = lastSelectionRef.current?.start ?? currentVal.length;
    let end = lastSelectionRef.current?.end ?? start;
    if (start > currentVal.length) start = currentVal.length;
    if (end > currentVal.length) end = currentVal.length;
    if (start > end) {
      const tmp = start;
      start = end;
      end = tmp;
    }

    const selectedText = currentVal.substring(start, end);
    const txt = linkText.trim() || selectedText.trim() || linkUrl.trim();

    let linkString = '';
    if (isHtml) {
      const targetAttr = linkNewTab ? ' target="_blank" rel="noopener noreferrer"' : '';
      linkString = `<a href="${linkUrl.trim()}"${targetAttr} style="color:#FF5E3A; text-decoration:underline;">${txt}</a>`;
    } else {
      linkString = `[${txt}](${linkUrl.trim()})`;
    }

    const updated = currentVal.substring(0, start) + linkString + currentVal.substring(end);
    const nextForm = { ...formData, [field]: updated };
    setFormData(nextForm);
    pushHistory(nextForm);

    setLinkModalOpen(false);
    setLinkUrl('');
    setLinkText('');
    setLinkNewTab(true);

    setTimeout(() => {
      if (textarea) {
        textarea.focus();
        const newCursor = start + linkString.length;
        textarea.setSelectionRange(newCursor, newCursor);
        lastSelectionRef.current = { start: newCursor, end: newCursor };
      }
    }, 50);
  };

  // Insert Image Modal Submit
  const handleInsertImageSubmit = () => {
    if (!imgUrlInput.trim()) {
      toast({ title: 'Please enter an image URL' });
      return;
    }
    const caption = imgCaption.trim() || 'Train journey photo';
    const alignStyle = imgAlign === 'left' ? 'float:left; margin:0 20px 20px 0;' : imgAlign === 'right' ? 'float:right; margin:0 0 20px 20px;' : 'margin:20px auto; text-align:center;';
    
    if (formData.content_type === 'html') {
      insertText(
        `\n<figure style="${alignStyle} max-width:${imgSize};">\n  <img src="${imgUrlInput.trim()}" alt="${caption}" style="width:100%; border-radius:12px; box-shadow:0 4px 6px -1px rgba(0,0,0,0.1);" />\n  <figcaption style="font-size:12px; color:#6B7280; margin-top:6px; text-align:center;">${caption}</figcaption>\n</figure>\n`
      );
    } else {
      insertText(`\n![${caption}](${imgUrlInput.trim()})\n*<small>${caption}</small>*\n`);
    }
    setImageModalOpen(false);
    setImgUrlInput('');
    setImgCaption('');
    setImgAlign('center');
    setImgSize('100%');
  };

  // Insert YouTube Video Modal Submit
  const handleInsertVideoSubmit = () => {
    if (!videoUrlInput.trim()) {
      toast({ title: 'Please enter a YouTube video URL or ID' });
      return;
    }
    const cleanInput = videoUrlInput.trim();
    let videoId = cleanInput;
    if (cleanInput.includes('v=')) {
      videoId = cleanInput.split('v=')[1].split('&')[0];
    } else if (cleanInput.includes('youtu.be/')) {
      videoId = cleanInput.split('youtu.be/')[1].split('?')[0];
    } else if (cleanInput.includes('embed/')) {
      videoId = cleanInput.split('embed/')[1].split('?')[0];
    }

    if (formData.content_type === 'html') {
      insertText(
        `\n<div style="position:relative; padding-bottom:56.25%; height:0; overflow:hidden; border-radius:14px; margin:24px 0; box-shadow:0 10px 25px -5px rgba(0,0,0,0.1);">\n  <iframe src="https://www.youtube-nocookie.com/embed/${videoId}" style="position:absolute; top:0; left:0; width:100%; height:100%; border:0;" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowfullscreen></iframe>\n</div>\n`
      );
    } else {
      insertText(
        `\n[![Watch Video](https://img.youtube.com/vi/${videoId}/hqdefault.jpg)](https://www.youtube.com/watch?v=${videoId})\n\n*(▶️ [Click here to watch on YouTube](https://www.youtube.com/watch?v=${videoId}))*\n`
      );
    }
    setVideoModalOpen(false);
    setVideoUrlInput('');
  };

  // Insert Table Modal Submit
  const handleInsertTableSubmit = () => {
    const rows = Math.max(1, Math.min(15, parseInt(tableRows) || 3));
    const cols = Math.max(1, Math.min(8, parseInt(tableCols) || 3));

    if (formData.content_type === 'html') {
      let htmlTable = `\n<table style="width:100%; border-collapse:collapse; margin:20px 0;">\n`;
      if (tableHasHeader) {
        htmlTable += `  <thead>\n    <tr style="background:#F1F5F9;">\n`;
        for (let c = 1; c <= cols; c++) {
          htmlTable += `      <th style="padding:10px; border:1px solid #CBD5E1; text-align:${tableAlign}; font-weight:700;">Header ${c}</th>\n`;
        }
        htmlTable += `    </tr>\n  </thead>\n`;
      }
      htmlTable += `  <tbody>\n`;
      for (let r = 1; r <= rows; r++) {
        htmlTable += `    <tr>\n`;
        for (let c = 1; c <= cols; c++) {
          htmlTable += `      <td style="padding:10px; border:1px solid #CBD5E1; text-align:${tableAlign};">Row ${r} Col ${c}</td>\n`;
        }
        htmlTable += `    </tr>\n`;
      }
      htmlTable += `  </tbody>\n</table>\n`;
      insertText(htmlTable);
    } else {
      let mdTable = '\n';
      // Header row
      mdTable += '| ' + Array.from({ length: cols }, (_, i) => `Header ${i + 1}`).join(' | ') + ' |\n';
      // Divider row
      const alignIndicator = tableAlign === 'center' ? ':---:' : tableAlign === 'right' ? '---:' : ':---';
      mdTable += '| ' + Array.from({ length: cols }, () => alignIndicator).join(' | ') + ' |\n';
      // Data rows
      for (let r = 1; r <= rows; r++) {
        mdTable += '| ' + Array.from({ length: cols }, (_, i) => `Row ${r} Col ${i + 1}`).join(' | ') + ' |\n';
      }
      mdTable += '\n';
      insertText(mdTable);
    }
    setTableModalOpen(false);
  };

  // Insert External Embed Submit
  const handleInsertEmbedSubmit = () => {
    if (!embedCodeInput.trim()) {
      toast({ title: 'Please paste embed code' });
      return;
    }
    const cleanCode = embedCodeInput.trim();
    if (formData.content_type === 'html') {
      insertText(`\n<div style="margin:24px 0; display:flex; justify-content:center;">\n  ${cleanCode}\n</div>\n`);
    } else {
      insertText(`\n${cleanCode}\n`);
    }
    setEmbedModalOpen(false);
    setEmbedCodeInput('');
  };

  // Apply Pre-built Template
  const applyTemplate = (template) => {
    const isHtml = formData.content_type === 'html';
    const targetContent = isHtml ? template.content_html : template.content_markdown;
    const currentContent = isHtml ? formData.content_html : formData.content_markdown;

    if (currentContent?.trim() && !window.confirm('Insert template? Existing content will be replaced.')) {
      return;
    }
    setFormData(prev => ({
      ...prev,
      [isHtml ? 'content_html' : 'content_markdown']: targetContent,
    }));
    toast({ title: `Loaded: ${template.title} (${isHtml ? 'HTML' : 'Markdown'})` });
  };

  // Save Blog Post
  const handleSavePost = async () => {
    if (!formData.title.trim()) {
      toast({ title: 'Please enter a post title' });
      return;
    }
    setSaving(true);
    try {
      const parsedTags = tagInput
        .split(',')
        .map((t) => t.trim())
        .filter(Boolean);

      const finalSlug = slugify(formData.slug || formData.title) || `story-${Date.now()}`;

      const payload = {
        ...formData,
        slug: finalSlug,
        tags: parsedTags.length > 0 ? parsedTags : ['Travel', 'Indian Railways'],
      };

      if (isEditing && currentId) {
        await axios.put(`${API}/admin/stories/${currentId}`, payload, {
          headers: { Authorization: `Bearer ${tok()}` },
        });
        toast({ title: 'Story updated successfully!' });
      } else {
        await axios.post(`${API}/admin/stories`, payload, {
          headers: { Authorization: `Bearer ${tok()}` },
        });
        toast({ title: 'New blog post published!' });
      }

      setEditorOpen(false);
      load();
    } catch (err) {
      toast({ title: 'Save failed', description: err.response?.data?.detail || err.message });
    } finally {
      setSaving(false);
    }
  };

  // Delete post
  const del = async (id) => {
    if (!window.confirm('Are you sure you want to permanently delete this story?')) return;
    try {
      await axios.delete(`${API}/admin/stories/${id}`, { headers: { Authorization: `Bearer ${tok()}` } });
      toast({ title: 'Story deleted' });
      load();
    } catch (e) {
      toast({ title: 'Delete failed', description: e.response?.data?.detail });
    }
  };

  // AI Generator function
  const generate = async (mode) => {
    if (mode === 'manual' && !manualTitle.trim()) {
      toast({ title: 'Enter a title for manual generation' });
      return;
    }
    setGenerating(true);
    try {
      const body = mode === 'manual' ? { title: manualTitle.trim() } : {};
      const r = await axios.post(`${API}/admin/stories/generate`, body, {
        headers: { Authorization: `Bearer ${tok()}` },
        timeout: 180000,
      });
      toast({
        title: 'Story generated',
        description: r.data?.story?.title?.slice(0, 80) || 'Done',
      });
      setManualTitle('');
      setAiModalOpen(false);
      load();
    } catch (e) {
      toast({ title: 'Generation failed', description: (e.response?.data?.detail || e.message)?.slice(0, 220) });
    } finally {
      setGenerating(false);
    }
  };

  const filteredItems = items.filter((s) => {
    const matchSearch =
      !search ||
      s.title?.toLowerCase().includes(search.toLowerCase()) ||
      s.slug?.toLowerCase().includes(search.toLowerCase()) ||
      s.excerpt?.toLowerCase().includes(search.toLowerCase());
    const matchCat = filterCat === 'ALL' || s.category === filterCat;
    return matchSearch && matchCat;
  });

  const activeContent = formData.content_type === 'html' ? formData.content_html : formData.content_markdown;
  const wordCount = activeContent ? activeContent.trim().split(/\s+/).filter(Boolean).length : 0;
  const charCount = activeContent ? activeContent.length : 0;
  const calcReadTime = `${Math.max(1, Math.ceil(wordCount / 200))} min read`;

  return (
    <div className="space-y-6">
      {/* Top Header */}
      <div className="flex items-center justify-between flex-wrap gap-4 bg-white p-5 rounded-2xl soft-shadow border border-gray-100">
        <div>
          <div className="flex items-center gap-2">
            <h2 className="text-2xl font-black text-[#0B2E5C] tracking-tight">Travel Stories &amp; Blog CMS</h2>
            <span className="text-xs bg-tp-violet-soft text-tp-violet-deep font-extrabold px-2.5 py-1 rounded-full border border-tp-violet/20">
              {items.length} Stories
            </span>
          </div>
          <p className="text-xs text-gray-500 mt-1">
            Write rich articles, publish travel guides, manage categories, and customize SEO.
          </p>
        </div>

        <div className="flex items-center gap-2.5 flex-wrap">
          <Button
            onClick={() => setCatModalOpen(true)}
            variant="outline"
            className="rounded-xl font-bold text-xs h-10 border-gray-300 text-gray-700 hover:bg-gray-50 flex items-center gap-1.5 shadow-sm"
          >
            <Tag size={15} className="text-tp-violet" /> Manage Categories
          </Button>

          <Button
            onClick={() => setAiModalOpen(true)}
            className="bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700 text-white font-bold text-xs rounded-xl h-10 flex items-center gap-1.5 shadow-md"
          >
            <Sparkles size={15} /> AI Generator
          </Button>

          <Button
            onClick={openCreate}
            data-testid="write-new-blog-btn"
            className="btn-coral text-white font-bold text-xs rounded-xl h-10 flex items-center gap-1.5 shadow-md"
          >
            <Plus size={16} /> Write New Blog Post
          </Button>
        </div>
      </div>

      {/* Filters Bar */}
      <div className="bg-white p-4 rounded-2xl soft-shadow border border-gray-100 flex items-center justify-between gap-4 flex-wrap">
        <div className="flex items-center gap-2 flex-1 min-w-[240px] max-w-md border-2 border-gray-100 rounded-xl focus-within:border-tp-violet px-3 py-1.5 bg-gray-50/50">
          <Search size={16} className="text-gray-400 shrink-0" />
          <input
            type="text"
            placeholder="Search stories by title, slug, keywords..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="bg-transparent border-none focus:outline-none text-xs w-full text-gray-800"
          />
          {search && (
            <button onClick={() => setSearch('')} className="text-xs text-gray-400 hover:text-gray-600 font-bold">
              ✕
            </button>
          )}
        </div>

        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-xs font-bold text-gray-500 flex items-center gap-1">
            <Layers size={13} /> Filter Category:
          </span>
          <select
            value={filterCat}
            onChange={(e) => setFilterCat(e.target.value)}
            className="text-xs font-bold text-gray-700 border border-gray-200 rounded-xl px-3 py-2 bg-white focus:outline-none focus:ring-1 focus:ring-tp-violet"
          >
            <option value="ALL">All Categories ({items.length})</option>
            {categories.map((c) => (
              <option key={c} value={c}>
                {c} ({items.filter((s) => s.category === c).length})
              </option>
            ))}
          </select>

          <Button
            variant="ghost"
            size="sm"
            onClick={load}
            disabled={loading}
            className="h-9 px-2.5 text-gray-500 hover:text-tp-violet rounded-xl"
            title="Reload Stories"
          >
            <RefreshCw size={14} className={loading ? 'animate-spin' : ''} />
          </Button>
        </div>
      </div>

      {/* Autosaved Draft Recovery Banner */}
      {hasAutosavedDraft && (
        <div className="bg-gradient-to-r from-amber-50 to-orange-50 border border-amber-200 rounded-2xl p-4 flex items-center justify-between flex-wrap gap-3 shadow-xs">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-xl bg-amber-500/10 text-amber-600 flex items-center justify-center shrink-0">
              <Clock size={18} />
            </div>
            <div>
              <h4 className="text-xs font-black text-[#0B2E5C]">Unsaved Draft Found</h4>
              <p className="text-[11px] text-gray-600">
                You have an autosaved article draft "{autosavedDraftData?.title || 'Untitled'}" from {autosavedDraftData?.autosavedAt || 'earlier'}.
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button
              size="sm"
              onClick={() => {
                restoreAutosavedDraft();
                setEditorOpen(true);
              }}
              className="bg-amber-600 hover:bg-amber-700 text-white font-bold text-xs h-8 px-3 rounded-lg shadow-xs"
            >
              Restore &amp; Open
            </Button>
            <Button
              size="sm"
              variant="ghost"
              onClick={discardAutosavedDraft}
              className="text-gray-500 hover:text-red-600 font-bold text-xs h-8 px-2.5 rounded-lg"
            >
              Discard
            </Button>
          </div>
        </div>
      )}

      {/* Stories List */}
      <div className="bg-white rounded-2xl soft-shadow border border-gray-100 overflow-hidden">
        {loading ? (
          <div className="p-4 space-y-3">
            {[1, 2, 3, 4, 5].map((n) => (
              <div key={n} className="flex items-center gap-4 p-3.5 rounded-2xl border border-gray-100 bg-white">
                <div className="w-24 h-16 rounded-xl shrink-0 shimmer-wave" />
                <div className="flex-1 space-y-2.5 min-w-0">
                  <div className="flex items-center gap-2">
                    <div className="w-20 h-4 rounded-md shimmer-wave-purple" />
                    <div className="w-16 h-4 rounded-md shimmer-wave" />
                  </div>
                  <div className="w-3/4 h-5 rounded-md shimmer-wave" />
                  <div className="w-1/2 h-3 rounded-md shimmer-wave" />
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <div className="w-8 h-8 rounded-lg shimmer-wave" />
                  <div className="w-8 h-8 rounded-lg shimmer-wave" />
                  <div className="w-8 h-8 rounded-lg shimmer-wave" />
                </div>
              </div>
            ))}
          </div>
        ) : filteredItems.length === 0 ? (
          <div className="text-center py-16 text-gray-400">
            <FileText size={36} className="mx-auto text-gray-300 mb-2" />
            <p className="font-bold text-gray-600">No stories found</p>
            <p className="text-xs text-gray-400 mt-1">Click <b>Write New Blog Post</b> above to create your first travel guide.</p>
          </div>
        ) : (
          <div className="divide-y divide-gray-100">
            {filteredItems.map((s) => (
              <div
                key={s.id}
                className="p-4 flex items-center gap-4 hover:bg-gray-50/80 transition-colors flex-wrap sm:flex-nowrap"
              >
                {s.hero_image ? (
                  <img
                    src={s.hero_image}
                    alt=""
                    className="w-24 h-16 object-cover rounded-xl border border-gray-200 shrink-0 bg-gray-100"
                    onError={(ev) => { ev.currentTarget.style.display = 'none'; }}
                  />
                ) : (
                  <div className="w-24 h-16 rounded-xl bg-gradient-to-br from-[#0B2E5C] to-[#1E4E8C] shrink-0 flex items-center justify-center text-white/30">
                    <Sparkles size={20} />
                  </div>
                )}

                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-[10px] font-extrabold bg-tp-violet-soft text-tp-violet-deep px-2 py-0.5 rounded-md uppercase">
                      {s.category || 'Travel Story'}
                    </span>
                    <span className={`text-[10px] font-extrabold px-2 py-0.5 rounded-md uppercase ${
                      s.status === 'published' ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'
                    }`}>
                      {s.status || 'published'}
                    </span>
                    {s.source === 'manual' ? (
                      <span className="text-[10px] font-bold bg-blue-50 text-blue-700 px-1.5 py-0.5 rounded">✍️ Manual</span>
                    ) : (
                      <span className="text-[10px] font-bold bg-purple-50 text-purple-700 px-1.5 py-0.5 rounded">✨ AI</span>
                    )}
                  </div>

                  <h4 className="font-extrabold text-[#0B2E5C] text-base mt-1 truncate hover:text-tp-orange">
                    {s.title}
                  </h4>

                  <div className="flex items-center gap-3 text-xs text-gray-500 mt-1 flex-wrap">
                    <span>Slug: <code className="bg-gray-100 px-1.5 py-0.5 rounded text-gray-700 text-[11px] font-mono">/stories/{s.slug}</code></span>
                    <span>• {s.word_count || 0} words</span>
                    <span>• {s.read_time || '3 min read'}</span>
                    <span>• {s.created_at ? new Date(s.created_at).toLocaleDateString() : ''}</span>
                  </div>
                </div>

                <div className="flex items-center gap-1.5 shrink-0 ml-auto">
                  <a
                    href={`/stories/${s.slug}`}
                    target="_blank"
                    rel="noreferrer"
                    title="View public page"
                    className="p-2 text-gray-500 hover:text-tp-violet hover:bg-tp-violet-soft rounded-lg transition-colors"
                  >
                    <ExternalLink size={16} />
                  </a>
                  <button
                    onClick={() => openEdit(s.id)}
                    title="Edit blog post"
                    className="p-2 text-gray-500 hover:text-tp-coral hover:bg-tp-coral-soft rounded-lg transition-colors"
                  >
                    <Edit3 size={16} />
                  </button>
                  <button
                    onClick={() => del(s.id)}
                    title="Delete"
                    className="p-2 text-gray-500 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                  >
                    <Trash2 size={16} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* ========================================================================= */}
      {/* CATEGORY MANAGER MODAL */}
      {/* ========================================================================= */}
      <Dialog open={catModalOpen} onOpenChange={(o) => !o && setCatModalOpen(false)}>
        <DialogContent className="max-w-md rounded-2xl p-6">
          <DialogHeader>
            <DialogTitle className="text-xl font-black text-[#0B2E5C] flex items-center gap-2">
              <Tag size={18} className="text-tp-violet" /> Manage Story Categories
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4 py-2">
            <div className="flex items-center gap-2">
              <Input
                placeholder="New Category Name (e.g. Hill Railways)"
                value={newCatName}
                onChange={(e) => setNewCatName(e.target.value)}
                className="h-10 text-xs rounded-xl"
              />
              <Button
                onClick={() => handleAddCategory(newCatName, false)}
                disabled={savingCat}
                className="btn-violet rounded-xl h-10 px-4 text-xs font-bold shrink-0"
              >
                {savingCat ? <Loader2 size={14} className="animate-spin" /> : <Plus size={14} />} Add
              </Button>
            </div>

            <div className="max-h-60 overflow-y-auto space-y-1.5 pr-1 divide-y divide-gray-50">
              {categories.map((cat) => (
                <div key={cat} className="flex items-center justify-between py-2 px-1">
                  {editingCatName === cat ? (
                    <div className="flex items-center gap-2 flex-1 mr-2">
                      <Input
                        value={editCatValue}
                        onChange={(e) => setEditCatValue(e.target.value)}
                        className="h-8 text-xs rounded-lg"
                        autoFocus
                      />
                      <Button
                        size="sm"
                        onClick={() => handleUpdateCategory(cat)}
                        disabled={updatingCat}
                        className="h-8 px-2.5 bg-green-600 hover:bg-green-700 text-white text-xs font-bold rounded-lg"
                      >
                        {updatingCat ? <Loader2 size={12} className="animate-spin" /> : <Check size={12} />}
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => setEditingCatName(null)}
                        className="h-8 px-2 text-xs text-gray-500 rounded-lg"
                      >
                        ✕
                      </Button>
                    </div>
                  ) : (
                    <div className="flex items-center gap-2">
                      <Tag size={12} className="text-tp-violet" />
                      <span className="text-xs font-bold text-gray-800">{cat}</span>
                    </div>
                  )}

                  {editingCatName !== cat && (
                    <div className="flex items-center gap-1">
                      <button
                        onClick={() => {
                          setEditingCatName(cat);
                          setEditCatValue(cat);
                        }}
                        className="p-1.5 text-gray-400 hover:text-tp-violet rounded"
                        title="Rename"
                      >
                        <Edit3 size={13} />
                      </button>
                      <button
                        onClick={() => handleDeleteCategory(cat)}
                        className="p-1.5 text-gray-400 hover:text-red-600 rounded"
                        title="Delete"
                      >
                        <Trash2 size={13} />
                      </button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setCatModalOpen(false)} className="rounded-xl text-xs font-bold">
              Done
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ========================================================================= */}
      {/* AI GENERATOR MODAL */}
      {/* ========================================================================= */}
      <Dialog open={aiModalOpen} onOpenChange={(o) => !o && setAiModalOpen(false)}>
        <DialogContent className="max-w-md rounded-2xl p-6">
          <DialogHeader>
            <DialogTitle className="text-xl font-black text-[#0B2E5C] flex items-center gap-2">
              <Sparkles size={18} className="text-purple-600" /> AI Story Generator
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4 py-2">
            <p className="text-xs text-gray-500">
              Generate an engaging, SEO-optimized travel story with image, headings, and tips using AI.
            </p>

            <div className="space-y-2">
              <label className="text-xs font-extrabold text-[#0B2E5C] uppercase tracking-wider block">
                Custom Topic / Title (Optional)
              </label>
              <Input
                placeholder="e.g. 5 Hidden Gems on the Konkan Railway in Monsoon"
                value={manualTitle}
                onChange={(e) => setManualTitle(e.target.value)}
                className="h-10 text-xs rounded-xl"
              />
            </div>

            <div className="grid grid-cols-2 gap-2 pt-2">
              <Button
                onClick={() => generate('manual')}
                disabled={generating || !manualTitle.trim()}
                className="bg-purple-600 hover:bg-purple-700 text-white font-bold text-xs rounded-xl h-10"
              >
                {generating ? <Loader2 size={14} className="animate-spin mr-1" /> : <Wand2 size={14} className="mr-1" />}
                Generate from Title
              </Button>
              <Button
                onClick={() => generate('auto')}
                disabled={generating}
                variant="outline"
                className="border-purple-300 text-purple-700 hover:bg-purple-50 font-bold text-xs rounded-xl h-10"
              >
                {generating ? <Loader2 size={14} className="animate-spin mr-1" /> : <Sparkles size={14} className="mr-1" />}
                Surprise Me (Auto)
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* ========================================================================= */}
      {/* LINK INSERT / EDIT MODAL */}
      {/* ========================================================================= */}
      <Dialog open={linkModalOpen} onOpenChange={(o) => !o && setLinkModalOpen(false)}>
        <DialogContent className="max-w-md rounded-2xl p-6">
          <DialogHeader>
            <DialogTitle className="text-lg font-black text-[#0B2E5C] flex items-center gap-2">
              <LinkIcon size={18} className="text-tp-violet" /> Insert Hyperlink
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <label className="text-xs font-bold text-gray-600 block mb-1">Link URL <span className="text-red-500">*</span></label>
              <Input
                placeholder="https://trainpanda.com/trains or https://example.com"
                value={linkUrl}
                onChange={(e) => setLinkUrl(e.target.value)}
                className="h-10 text-xs rounded-xl font-mono"
              />
            </div>
            <div>
              <label className="text-xs font-bold text-gray-600 block mb-1">Display Text (Anchor Text)</label>
              <Input
                placeholder="e.g. Check Live Status on TrainPanda"
                value={linkText}
                onChange={(e) => setLinkText(e.target.value)}
                className="h-10 text-xs rounded-xl"
              />
            </div>
            <div className="flex items-center gap-2 pt-1">
              <input
                type="checkbox"
                id="linkNewTabCheck"
                checked={linkNewTab}
                onChange={(e) => setLinkNewTab(e.target.checked)}
                className="rounded text-tp-violet focus:ring-tp-violet cursor-pointer"
              />
              <label htmlFor="linkNewTabCheck" className="text-xs font-bold text-gray-700 cursor-pointer select-none">
                Open link in a new tab (<code className="text-[11px] font-mono">target="_blank"</code>)
              </label>
            </div>
          </div>
          <DialogFooter className="flex gap-2">
            <Button variant="outline" onClick={() => setLinkModalOpen(false)} className="rounded-xl text-xs font-bold">
              Cancel
            </Button>
            <Button onClick={handleInsertLinkSubmit} className="btn-violet rounded-xl text-xs font-bold">
              Insert Link
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ========================================================================= */}
      {/* IMAGE URL & FORMATTING MODAL */}
      {/* ========================================================================= */}
      <Dialog open={imageModalOpen} onOpenChange={(o) => !o && setImageModalOpen(false)}>
        <DialogContent className="max-w-lg rounded-2xl p-6">
          <DialogHeader>
            <DialogTitle className="text-lg font-black text-[#0B2E5C] flex items-center gap-2">
              <ImageIcon size={18} className="text-tp-violet" /> Insert &amp; Align Image
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <label className="text-xs font-bold text-gray-600 block mb-1">Image URL <span className="text-red-500">*</span></label>
              <Input
                placeholder="https://images.unsplash.com/... or uploaded photo link"
                value={imgUrlInput}
                onChange={(e) => setImgUrlInput(e.target.value)}
                className="h-10 text-xs rounded-xl font-mono"
              />
            </div>
            <div>
              <label className="text-xs font-bold text-gray-600 block mb-1">Image Caption / Alt Text</label>
              <Input
                placeholder="e.g. Vande Bharat Express traversing Western Ghats"
                value={imgCaption}
                onChange={(e) => setImgCaption(e.target.value)}
                className="h-10 text-xs rounded-xl"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs font-bold text-gray-600 block mb-1">Image Size / Width</label>
                <select
                  value={imgSize}
                  onChange={(e) => setImgSize(e.target.value)}
                  className="w-full h-10 border border-gray-200 rounded-xl px-3 text-xs font-bold text-gray-800 bg-white"
                >
                  <option value="100%">100% (Full Width Responsive)</option>
                  <option value="75%">75% (Medium Width)</option>
                  <option value="50%">50% (Half Width)</option>
                </select>
              </div>
              <div>
                <label className="text-xs font-bold text-gray-600 block mb-1">Image Alignment</label>
                <select
                  value={imgAlign}
                  onChange={(e) => setImgAlign(e.target.value)}
                  className="w-full h-10 border border-gray-200 rounded-xl px-3 text-xs font-bold text-gray-800 bg-white"
                >
                  <option value="center">Center (Centered Block)</option>
                  <option value="left">Left (Float Left with text wrap)</option>
                  <option value="right">Right (Float Right with text wrap)</option>
                </select>
              </div>
            </div>

            {imgUrlInput && (
              <div className="mt-2 rounded-xl overflow-hidden border border-gray-200 bg-gray-50 max-h-36 flex items-center justify-center">
                <img src={imgUrlInput} alt="Preview" className="max-h-36 object-contain" onError={(e) => { e.currentTarget.style.display = 'none'; }} />
              </div>
            )}
          </div>
          <DialogFooter className="flex gap-2">
            <Button variant="outline" onClick={() => setImageModalOpen(false)} className="rounded-xl text-xs font-bold">
              Cancel
            </Button>
            <Button onClick={handleInsertImageSubmit} className="btn-violet rounded-xl text-xs font-bold">
              Insert Image
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ========================================================================= */}
      {/* YOUTUBE / VIDEO EMBED MODAL */}
      {/* ========================================================================= */}
      <Dialog open={videoModalOpen} onOpenChange={(o) => !o && setVideoModalOpen(false)}>
        <DialogContent className="max-w-md rounded-2xl p-6">
          <DialogHeader>
            <DialogTitle className="text-lg font-black text-[#0B2E5C] flex items-center gap-2">
              <Youtube size={18} className="text-red-600" /> Embed YouTube / Video
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <p className="text-xs text-gray-500">
              Paste full YouTube video URL (e.g. <code className="font-mono bg-gray-100 px-1 py-0.5 rounded">https://www.youtube.com/watch?v=dQw4w9WgXcQ</code>) or YouTube Short link.
            </p>
            <div>
              <label className="text-xs font-bold text-gray-600 block mb-1">YouTube URL or Video ID <span className="text-red-500">*</span></label>
              <Input
                placeholder="https://youtu.be/..."
                value={videoUrlInput}
                onChange={(e) => setVideoUrlInput(e.target.value)}
                className="h-10 text-xs rounded-xl font-mono"
              />
            </div>
          </div>
          <DialogFooter className="flex gap-2">
            <Button variant="outline" onClick={() => setVideoModalOpen(false)} className="rounded-xl text-xs font-bold">
              Cancel
            </Button>
            <Button onClick={handleInsertVideoSubmit} className="bg-red-600 hover:bg-red-700 text-white rounded-xl text-xs font-bold">
              Embed Video
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ========================================================================= */}
      {/* CUSTOM TABLE GENERATOR MODAL */}
      {/* ========================================================================= */}
      <Dialog open={tableModalOpen} onOpenChange={(o) => !o && setTableModalOpen(false)}>
        <DialogContent className="max-w-md rounded-2xl p-6">
          <DialogHeader>
            <DialogTitle className="text-lg font-black text-[#0B2E5C] flex items-center gap-2">
              <TableIcon size={18} className="text-tp-violet" /> Insert Custom Table
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs font-bold text-gray-600 block mb-1">Number of Rows</label>
                <Input
                  type="number"
                  min={1}
                  max={15}
                  value={tableRows}
                  onChange={(e) => setTableRows(e.target.value)}
                  className="h-10 text-xs rounded-xl"
                />
              </div>
              <div>
                <label className="text-xs font-bold text-gray-600 block mb-1">Number of Columns</label>
                <Input
                  type="number"
                  min={1}
                  max={8}
                  value={tableCols}
                  onChange={(e) => setTableCols(e.target.value)}
                  className="h-10 text-xs rounded-xl"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs font-bold text-gray-600 block mb-1">Cell Text Alignment</label>
                <select
                  value={tableAlign}
                  onChange={(e) => setTableAlign(e.target.value)}
                  className="w-full h-10 border border-gray-200 rounded-xl px-3 text-xs font-bold text-gray-800 bg-white"
                >
                  <option value="left">Left Aligned</option>
                  <option value="center">Center Aligned</option>
                  <option value="right">Right Aligned</option>
                </select>
              </div>
              <div className="flex items-end pb-2">
                <label className="flex items-center gap-2 text-xs font-bold text-gray-700 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={tableHasHeader}
                    onChange={(e) => setTableHasHeader(e.target.checked)}
                    className="rounded text-tp-violet focus:ring-tp-violet"
                  />
                  Include Header Row
                </label>
              </div>
            </div>
          </div>
          <DialogFooter className="flex gap-2">
            <Button variant="outline" onClick={() => setTableModalOpen(false)} className="rounded-xl text-xs font-bold">
              Cancel
            </Button>
            <Button onClick={handleInsertTableSubmit} className="btn-violet rounded-xl text-xs font-bold">
              Generate Table
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ========================================================================= */}
      {/* EXTERNAL CONTENT / IFRAME EMBED MODAL */}
      {/* ========================================================================= */}
      <Dialog open={embedModalOpen} onOpenChange={(o) => !o && setEmbedModalOpen(false)}>
        <DialogContent className="max-w-md rounded-2xl p-6">
          <DialogHeader>
            <DialogTitle className="text-lg font-black text-[#0B2E5C] flex items-center gap-2">
              <Code2 size={18} className="text-tp-violet" /> Embed External Widget / Code
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <p className="text-xs text-gray-500">
              Paste an <code className="font-mono bg-gray-100 px-1 py-0.5 rounded">&lt;iframe&gt;</code>, Google Map embed, Twitter/X post, or custom HTML widget.
            </p>
            <Textarea
              rows={4}
              placeholder="<iframe src='...' width='100%' height='350' style='border:0;'></iframe>"
              value={embedCodeInput}
              onChange={(e) => setEmbedCodeInput(e.target.value)}
              className="text-xs font-mono rounded-xl"
            />
          </div>
          <DialogFooter className="flex gap-2">
            <Button variant="outline" onClick={() => setEmbedModalOpen(false)} className="rounded-xl text-xs font-bold">
              Cancel
            </Button>
            <Button onClick={handleInsertEmbedSubmit} className="btn-violet rounded-xl text-xs font-bold">
              Insert Embed
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ========================================================================= */}
      {/* COLOR & HIGHLIGHT PICKER MODAL */}
      {/* ========================================================================= */}
      <Dialog open={colorPickerOpen} onOpenChange={(o) => !o && setColorPickerOpen(false)}>
        <DialogContent className="max-w-sm rounded-2xl p-5">
          <DialogHeader>
            <DialogTitle className="text-base font-black text-[#0B2E5C] flex items-center gap-2">
              <Palette size={16} className="text-tp-violet" /> Text Color &amp; Highlight
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <span className="text-xs font-extrabold text-[#0B2E5C] uppercase tracking-wider block mb-2">Text Color</span>
              <div className="grid grid-cols-4 gap-2">
                {[
                  { name: 'Default Dark', code: '#1E293B', label: 'Dark' },
                  { name: 'Train Coral', code: '#FF5E3A', label: 'Coral' },
                  { name: 'Railway Blue', code: '#0B2E5C', label: 'Navy' },
                  { name: 'Emerald', code: '#16A34A', label: 'Green' },
                  { name: 'Purple', code: '#7C3AED', label: 'Purple' },
                  { name: 'Amber', code: '#D97706', label: 'Amber' },
                  { name: 'Crimson', code: '#DC2626', label: 'Red' },
                  { name: 'Teal Blue', code: '#0D9488', label: 'Teal' },
                ].map((c) => (
                  <button
                    key={c.code}
                    type="button"
                    onClick={() => applyTextColor(c.code)}
                    className="flex items-center gap-1.5 p-1.5 rounded-lg border border-gray-200 hover:border-tp-violet text-[11px] font-bold transition-all hover:bg-gray-50"
                  >
                    <span className="w-3.5 h-3.5 rounded-full shrink-0 shadow-xs" style={{ backgroundColor: c.code }} />
                    <span className="truncate text-gray-700">{c.label}</span>
                  </button>
                ))}
              </div>
            </div>

            <hr className="border-gray-100" />

            <div>
              <span className="text-xs font-extrabold text-[#0B2E5C] uppercase tracking-wider block mb-2">Highlight Background</span>
              <div className="grid grid-cols-3 gap-2">
                {[
                  { name: 'Yellow', code: '#FEF08A', label: '🟡 Yellow' },
                  { name: 'Green', code: '#BBF7D0', label: '🟢 Green' },
                  { name: 'Pink', code: '#FBCFE8', label: '🌸 Pink' },
                  { name: 'Cyan Blue', code: '#BAE6FD', label: '💧 Blue' },
                  { name: 'Peach', code: '#FED7AA', label: '🍑 Peach' },
                  { name: 'Lavender', code: '#E9D5FF', label: '💜 Purple' },
                ].map((bg) => (
                  <button
                    key={bg.code}
                    type="button"
                    onClick={() => applyHighlight(bg.code)}
                    className="p-1.5 rounded-lg border border-gray-200 hover:border-tp-violet text-[11px] font-bold text-center transition-all hover:shadow-xs"
                    style={{ backgroundColor: bg.code }}
                  >
                    {bg.label}
                  </button>
                ))}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setColorPickerOpen(false)} className="rounded-xl text-xs font-bold w-full">
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ========================================================================= */}
      {/* FULL-FEATURED RICH BLOG POST EDITOR MODAL */}
      {/* ========================================================================= */}
      <Dialog open={editorOpen} onOpenChange={(o) => !o && setEditorOpen(false)}>
        <DialogContent
          className={
            isFullscreen
              ? '!fixed !inset-0 !left-0 !top-0 !translate-x-0 !translate-y-0 !w-screen !h-screen !max-w-none !max-h-none !rounded-none !p-0 !m-0 !gap-0 !flex !flex-col !bg-[#F8FAFC] !z-50 !overflow-hidden shadow-none'
              : '!fixed !left-[50%] !top-[50%] !translate-x-[-50%] !translate-y-[-50%] !w-[96vw] !max-w-6xl !h-[92vh] !max-h-[92vh] !rounded-2xl !p-0 !m-0 !gap-0 !flex !flex-col !bg-[#F8FAFC] !z-50 !overflow-hidden shadow-2xl'
          }
        >
          {/* Editor Header Bar */}
          <div className="px-5 py-3 bg-white border-b border-gray-200 flex items-center justify-between flex-wrap gap-3 pr-14 shrink-0">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-xl bg-tp-violet/10 text-tp-violet flex items-center justify-center font-bold">
                <FileText size={18} />
              </div>
              <div>
                <div className="flex items-center gap-2">
                  <h3 className="font-extrabold text-[#0B2E5C] text-base leading-tight">
                    {isEditing ? 'Edit Blog Story' : 'Create New Travel Story'}
                  </h3>
                  {lastAutosavedTime && (
                    <span className="text-[10px] bg-slate-100 text-slate-600 px-2 py-0.5 rounded-full font-mono border border-slate-200">
                      💾 Autosaved at {lastAutosavedTime}
                    </span>
                  )}
                </div>
                <div className="flex items-center gap-3 text-xs text-gray-500 mt-0.5">
                  <span>📊 {wordCount} words</span>
                  <span>⏱️ {calcReadTime}</span>
                  <span>🔤 {charCount} chars</span>
                </div>
              </div>
            </div>

            <div className="flex items-center gap-2 flex-wrap">
              {/* Fullscreen Toggle */}
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => setIsFullscreen(!isFullscreen)}
                className="h-9 w-9 p-0 rounded-xl border-gray-300 text-gray-600 hover:text-tp-violet"
                title={isFullscreen ? 'Exit Full Screen' : 'Full Screen Editor'}
              >
                {isFullscreen ? <Minimize2 size={16} /> : <Maximize2 size={16} />}
              </Button>

              <Button
                onClick={handleSavePost}
                disabled={saving}
                className="btn-coral text-white font-extrabold text-xs h-9 px-5 rounded-xl flex items-center gap-1.5 shadow-md"
              >
                {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
                {isEditing ? 'Update Story' : 'Publish Story'}
              </Button>
            </div>
          </div>

          {/* Main Scrollable Content Body */}
          <div className="flex-1 overflow-y-auto p-6 space-y-6">
            {/* 1. Essential Meta (Title, Slug, Category, Status) */}
            <div className="bg-white p-5 rounded-2xl soft-shadow border border-gray-100 space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-12 gap-4">
                {/* Title */}
                <div className="md:col-span-8">
                  <label className="text-xs font-extrabold text-[#0B2E5C] uppercase tracking-wider block mb-1">
                    Article Title <span className="text-red-500">*</span>
                  </label>
                  <Input
                    placeholder="e.g. Complete Guide to Vande Bharat Express: Routes, Fares & Food"
                    value={formData.title}
                    onChange={(e) => handleTitleChange(e.target.value)}
                    className="h-11 text-base font-bold rounded-xl border-gray-200 focus-visible:ring-tp-violet"
                  />
                </div>

                {/* Category */}
                <div className="md:col-span-4">
                  <div className="flex items-center justify-between mb-1">
                    <label className="text-xs font-extrabold text-[#0B2E5C] uppercase tracking-wider">Category</label>
                    <button
                      type="button"
                      onClick={() => setShowInlineAddCat(!showInlineAddCat)}
                      className="text-[11px] font-bold text-tp-violet hover:underline flex items-center gap-0.5"
                    >
                      <PlusCircle size={11} /> New
                    </button>
                  </div>

                  {showInlineAddCat ? (
                    <div className="flex items-center gap-1">
                      <Input
                        placeholder="Category name"
                        value={inlineCatName}
                        onChange={(e) => setInlineCatName(e.target.value)}
                        className="h-11 text-xs rounded-xl"
                      />
                      <Button
                        size="sm"
                        onClick={() => handleAddCategory(inlineCatName, true)}
                        disabled={savingCat}
                        className="btn-violet h-11 px-3 text-xs font-bold rounded-xl shrink-0"
                      >
                        {savingCat ? <Loader2 size={12} className="animate-spin" /> : 'Save'}
                      </Button>
                    </div>
                  ) : (
                    <select
                      value={formData.category}
                      onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                      className="w-full h-11 border-2 border-gray-200 rounded-xl px-3 text-xs font-bold text-gray-800 bg-white focus:outline-none focus:border-tp-violet"
                    >
                      {categories.map((c) => (
                        <option key={c} value={c}>
                          {c}
                        </option>
                      ))}
                    </select>
                  )}
                </div>
              </div>

              {/* Slug, Author, Read Time, Status */}
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4 pt-1">
                <div>
                  <div className="flex items-center justify-between mb-1">
                    <label className="text-xs font-bold text-gray-500 uppercase tracking-wider">URL Slug</label>
                    <button
                      type="button"
                      onClick={regenerateSlugFromTitle}
                      className="text-[11px] font-bold text-tp-violet hover:underline flex items-center gap-0.5"
                      title="Auto-generate clean slug from current title"
                    >
                      <RefreshCw size={10} /> Auto-Sync
                    </button>
                  </div>
                  <div className="flex items-center border border-gray-200 rounded-xl px-2.5 bg-gray-50 h-10 focus-within:border-tp-violet focus-within:bg-white transition-all">
                    <span className="text-gray-400 text-xs font-mono select-none">/stories/</span>
                    <input
                      value={formData.slug}
                      onChange={(e) => handleSlugChange(e.target.value)}
                      placeholder="my-post-url"
                      className="bg-transparent border-0 text-xs font-mono text-gray-800 w-full focus:outline-none font-bold"
                    />
                  </div>
                </div>

                <div>
                  <label className="text-xs font-bold text-gray-500 uppercase tracking-wider block mb-1">Author</label>
                  <Input
                    value={formData.author}
                    onChange={(e) => setFormData({ ...formData, author: e.target.value })}
                    className="h-10 text-xs rounded-xl"
                  />
                </div>

                <div>
                  <label className="text-xs font-bold text-gray-500 uppercase tracking-wider block mb-1">Read Time</label>
                  <Input
                    value={formData.read_time || calcReadTime}
                    onChange={(e) => setFormData({ ...formData, read_time: e.target.value })}
                    className="h-10 text-xs rounded-xl"
                  />
                </div>

                <div>
                  <label className="text-xs font-bold text-gray-500 uppercase tracking-wider block mb-1">Status</label>
                  <select
                    value={formData.status}
                    onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                    className="w-full h-10 border border-gray-200 rounded-xl px-3 text-xs font-bold text-gray-800 bg-white"
                  >
                    <option value="published">🟢 Published (Live)</option>
                    <option value="draft">🟡 Draft (Hidden)</option>
                  </select>
                </div>
              </div>
            </div>

            {/* 2. Featured Hero Image */}
            <div className="bg-white p-5 rounded-2xl soft-shadow border border-gray-100">
              <div className="flex items-center justify-between mb-2">
                <label className="text-xs font-extrabold text-[#0B2E5C] uppercase tracking-wider flex items-center gap-1.5">
                  <ImageIcon size={15} className="text-tp-violet" /> Featured Hero Image
                </label>
                <div className="flex items-center gap-2">
                  <input
                    type="file"
                    accept="image/*"
                    ref={fileInputRef}
                    onChange={(e) => handleImageUpload(e, 'hero')}
                    className="hidden"
                  />
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => fileInputRef.current?.click()}
                    disabled={uploadingImg}
                    className="h-8 text-xs font-bold rounded-lg border-tp-violet text-tp-violet hover:bg-tp-violet-soft"
                  >
                    {uploadingImg ? <Loader2 size={12} className="animate-spin mr-1" /> : <Upload size={12} className="mr-1" />}
                    Upload Hero Photo
                  </Button>
                </div>
              </div>

              <Input
                placeholder="Image URL (e.g. https://images.unsplash.com/photo-... or uploaded file)"
                value={formData.hero_image}
                onChange={(e) => setFormData({ ...formData, hero_image: e.target.value })}
                className="h-10 text-xs font-mono rounded-xl bg-gray-50"
              />

              {formData.hero_image && (
                <div className="mt-3 relative rounded-xl overflow-hidden border border-gray-200 bg-black/5 max-h-48 w-full flex items-center justify-center">
                  <img
                    src={formData.hero_image}
                    alt="Hero Preview"
                    className="w-full h-48 object-cover"
                    onError={(e) => { e.currentTarget.style.display = 'none'; }}
                  />
                  <button
                    onClick={() => setFormData({ ...formData, hero_image: '' })}
                    className="absolute top-2 right-2 bg-black/70 hover:bg-red-600 text-white p-1.5 rounded-full text-xs shadow-lg transition-colors"
                    title="Remove image"
                  >
                    <X size={14} />
                  </button>
                </div>
              )}
            </div>

            {/* 3. Comprehensive Article Content & Editor */}
            <div className="bg-white rounded-2xl soft-shadow border border-gray-100 overflow-hidden flex flex-col">
              {/* Section Bar */}
              <div className="px-5 py-3 border-b border-gray-100 flex items-center justify-between flex-wrap gap-2 bg-white">
                <label className="text-xs font-extrabold text-[#0B2E5C] uppercase tracking-wider flex items-center gap-1.5">
                  <FileText size={15} className="text-tp-violet" /> Story Content (Rich Markdown / HTML)
                </label>

                <div className="flex items-center gap-3">
                  {/* View mode toggle (Edit Only / Split / Preview) */}
                  <div className="flex items-center bg-gray-100 p-1 rounded-xl border border-gray-200">
                    <button
                      type="button"
                      onClick={() => setViewMode('write')}
                      className={`px-3 py-1 rounded-lg text-xs font-bold transition-all ${
                        viewMode === 'write' ? 'bg-white text-tp-violet shadow-xs' : 'text-gray-600 hover:text-gray-900'
                      }`}
                    >
                      📝 Edit Only
                    </button>
                    <button
                      type="button"
                      onClick={() => setViewMode('split')}
                      className={`px-3 py-1 rounded-lg text-xs font-bold transition-all ${
                        viewMode === 'split' ? 'bg-white text-tp-violet shadow-xs' : 'text-gray-600 hover:text-gray-900'
                      }`}
                    >
                      ⚡ Split View
                    </button>
                    <button
                      type="button"
                      onClick={() => setViewMode('preview')}
                      className={`px-3 py-1 rounded-lg text-xs font-bold transition-all ${
                        viewMode === 'preview' ? 'bg-white text-tp-violet shadow-xs' : 'text-gray-600 hover:text-gray-900'
                      }`}
                    >
                      👁️ Full Preview
                    </button>
                  </div>
                </div>
              </div>

              {/* Comprehensive Toolbar */}
              <div className="bg-[#F8FAFC] px-5 py-2.5 border-b border-gray-200 flex flex-col gap-2">
                {/* Row 1: Templates, Undo/Redo, Mode Switch */}
                <div className="flex items-center justify-between flex-wrap gap-2 pb-1.5 border-b border-gray-200/60">
                  <div className="flex items-center gap-1.5 flex-wrap">
                    <span className="text-[11px] font-bold text-gray-500 flex items-center gap-1">
                      <BookOpen size={13} className="text-tp-violet" /> Ready Templates:
                    </span>
                    {TEMPLATES.map((tmpl) => (
                      <button
                        key={tmpl.id}
                        type="button"
                        onClick={() => applyTemplate(tmpl)}
                        className="text-[11px] px-2.5 py-1 bg-white hover:bg-violet-50 text-gray-700 hover:text-tp-violet border border-gray-200 rounded-lg font-bold flex items-center gap-1 transition-colors shadow-2xs"
                      >
                        <tmpl.icon size={11} className="text-tp-coral" /> {tmpl.title.split(' ')[1]} Guide
                      </button>
                    ))}
                  </div>

                  <div className="flex items-center gap-2">
                    {/* Undo / Redo */}
                    <div className="flex items-center bg-white rounded-lg border border-gray-200 p-0.5 shadow-2xs">
                      <button
                        type="button"
                        onClick={handleUndo}
                        disabled={historyIndex <= 0}
                        className="p-1 hover:bg-gray-100 disabled:opacity-40 rounded text-gray-700 font-bold"
                        title="Undo (Ctrl+Z)"
                      >
                        <Undo2 size={13} />
                      </button>
                      <button
                        type="button"
                        onClick={handleRedo}
                        disabled={historyIndex >= history.length - 1}
                        className="p-1 hover:bg-gray-100 disabled:opacity-40 rounded text-gray-700 font-bold"
                        title="Redo (Ctrl+Y)"
                      >
                        <Redo2 size={13} />
                      </button>
                    </div>

                    <div className="flex items-center gap-1 bg-white p-0.5 rounded-lg border border-gray-200 shadow-2xs">
                      <button
                        type="button"
                        onClick={() => {
                          setFormData((prev) => ({
                            ...prev,
                            content_type: 'markdown',
                            content_markdown: prev.content_markdown || prev.content_html || '',
                          }));
                        }}
                        className={`px-2.5 py-0.5 rounded text-xs font-bold transition-all ${
                          formData.content_type === 'markdown' ? 'bg-tp-violet text-white shadow-xs' : 'text-gray-600 hover:text-gray-900'
                        }`}
                      >
                        Markdown
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          setFormData((prev) => ({
                            ...prev,
                            content_type: 'html',
                            content_html: prev.content_html || prev.content_markdown || '',
                          }));
                        }}
                        className={`px-2.5 py-0.5 rounded text-xs font-bold transition-all ${
                          formData.content_type === 'html' ? 'bg-tp-coral text-white shadow-xs' : 'text-gray-600 hover:text-gray-900'
                        }`}
                      >
                        HTML
                      </button>
                    </div>
                  </div>
                </div>

                {/* Row 2: Rich Formatting Controls */}
                <div className="flex items-center gap-1.5 flex-wrap text-xs text-gray-700">
                  {/* Clean Text Format / Size Dropdown */}
                  <div className="flex items-center bg-white rounded-lg border border-gray-200 px-2 py-0.5 shadow-2xs">
                    <select
                      onChange={(e) => {
                        const val = e.target.value;
                        if (!val) return;
                        if (val === 'h1') applyHeading(1);
                        else if (val === 'h2') applyHeading(2);
                        else if (val === 'h3') applyHeading(3);
                        else if (val === 'h4') applyHeading(4);
                        else if (val === 'p') {
                          if (formData.content_type === 'html') insertText('<p>', '</p>\n', 'Paragraph text');
                          else insertText('', '\n', 'Paragraph text');
                        } else if (val === 'lead') {
                          if (formData.content_type === 'html') insertText('<p style="font-size:18px; line-height:1.6; font-weight:500;">', '</p>\n', 'Lead text');
                          else insertText('### ', '\n', 'Lead Highlight');
                        } else if (val === 'small') {
                          if (formData.content_type === 'html') insertText('<p style="font-size:13px; color:#4B5563;">', '</p>\n', 'Small note');
                          else insertText('*', '*', 'Small note');
                        }
                        e.target.value = '';
                      }}
                      className="text-xs font-bold text-gray-700 bg-transparent border-none focus:outline-none cursor-pointer"
                      defaultValue=""
                      title="Font Size & Heading Level"
                    >
                      <option value="" disabled>Text Size / Style</option>
                      <option value="p">Normal (16px)</option>
                      <option value="h1">Heading 1 (32px)</option>
                      <option value="h2">Heading 2 (24px)</option>
                      <option value="h3">Heading 3 (20px)</option>
                      <option value="h4">Heading 4 (16px)</option>
                      <option value="lead">Lead Text (18px)</option>
                      <option value="small">Small Note (13px)</option>
                    </select>
                  </div>

                  {/* Headings */}
                  <div className="flex items-center bg-white rounded-lg border border-gray-200 p-0.5 shadow-2xs">
                    <button
                      type="button"
                      onMouseDown={(e) => e.preventDefault()}
                      onClick={() => applyHeading(1)}
                      className="p-1 hover:bg-gray-100 rounded font-black text-xs text-tp-violet"
                      title="Heading 1 (H1)"
                    >
                      <Heading1 size={14} />
                    </button>
                    <button
                      type="button"
                      onMouseDown={(e) => e.preventDefault()}
                      onClick={() => applyHeading(2)}
                      className="p-1 hover:bg-gray-100 rounded font-black text-xs text-tp-violet"
                      title="Heading 2 (H2)"
                    >
                      <Heading2 size={14} />
                    </button>
                    <button
                      type="button"
                      onMouseDown={(e) => e.preventDefault()}
                      onClick={() => applyHeading(3)}
                      className="p-1 hover:bg-gray-100 rounded font-black text-xs text-tp-violet"
                      title="Heading 3 (H3)"
                    >
                      <Heading3 size={14} />
                    </button>
                    <button
                      type="button"
                      onMouseDown={(e) => e.preventDefault()}
                      onClick={() => applyHeading(4)}
                      className="p-1 hover:bg-gray-100 rounded font-black text-xs text-tp-violet"
                      title="Heading 4 (H4)"
                    >
                      <Heading4 size={14} />
                    </button>
                  </div>

                  {/* Text Style: Bold, Italic, Underline, Strikethrough, Code */}
                  <div className="flex items-center bg-white rounded-lg border border-gray-200 p-0.5 shadow-2xs">
                    <button
                      type="button"
                      onClick={() =>
                        formData.content_type === 'html'
                          ? insertText('<strong>', '</strong>', 'bold text')
                          : insertText('**', '**', 'bold text')
                      }
                      className="p-1 hover:bg-gray-100 rounded font-bold"
                      title="Bold (Ctrl+B)"
                    >
                      <Bold size={13} />
                    </button>
                    <button
                      type="button"
                      onClick={() =>
                        formData.content_type === 'html'
                          ? insertText('<em>', '</em>', 'italic text')
                          : insertText('*', '*', 'italic text')
                      }
                      className="p-1 hover:bg-gray-100 rounded font-bold"
                      title="Italic (Ctrl+I)"
                    >
                      <Italic size={13} />
                    </button>
                    <button
                      type="button"
                      onClick={() =>
                        formData.content_type === 'html'
                          ? insertText('<u>', '</u>', 'underlined text')
                          : insertText('_', '_', 'underlined text')
                      }
                      className="p-1 hover:bg-gray-100 rounded font-bold"
                      title="Underline"
                    >
                      <Underline size={13} />
                    </button>
                    <button
                      type="button"
                      onClick={() =>
                        formData.content_type === 'html'
                          ? insertText('<del>', '</del>', 'strikethrough text')
                          : insertText('~~', '~~', 'strikethrough text')
                      }
                      className="p-1 hover:bg-gray-100 rounded font-bold"
                      title="Strikethrough"
                    >
                      <Strikethrough size={13} />
                    </button>
                    <button
                      type="button"
                      onClick={() =>
                        formData.content_type === 'html'
                          ? insertText('<code>', '</code>', 'code')
                          : insertText('`', '`', 'code')
                      }
                      className="p-1 hover:bg-gray-100 rounded font-mono font-bold"
                      title="Inline Code"
                    >
                      <Code size={13} />
                    </button>
                  </div>

                  {/* Color & Highlight Picker Trigger */}
                  <div className="flex items-center bg-white rounded-lg border border-gray-200 p-0.5 shadow-2xs">
                    <button
                      type="button"
                      onClick={() => setColorPickerOpen(true)}
                      className="px-2 py-0.5 hover:bg-gray-100 rounded flex items-center gap-1 text-tp-violet font-bold text-[11px]"
                      title="Text Color & Background Highlight"
                    >
                      <Palette size={13} /> Color / Highlight
                    </button>
                  </div>

                  {/* Text Alignment */}
                  <div className="flex items-center bg-white rounded-lg border border-gray-200 p-0.5 shadow-2xs">
                    <button
                      type="button"
                      onClick={() =>
                        formData.content_type === 'html'
                          ? insertText('<div style="text-align:left;">\n  ', '\n</div>\n', 'Left aligned text')
                          : insertText('[left]', '[/left]', 'Left aligned text')
                      }
                      className="p-1 hover:bg-gray-100 rounded text-gray-700"
                      title="Align Left"
                    >
                      <AlignLeft size={13} />
                    </button>
                    <button
                      type="button"
                      onClick={() =>
                        formData.content_type === 'html'
                          ? insertText('<div style="text-align:center;">\n  ', '\n</div>\n', 'Centered text')
                          : insertText('[center]', '[/center]', 'Centered text')
                      }
                      className="p-1 hover:bg-gray-100 rounded text-gray-700"
                      title="Align Center"
                    >
                      <AlignCenter size={13} />
                    </button>
                    <button
                      type="button"
                      onClick={() =>
                        formData.content_type === 'html'
                          ? insertText('<div style="text-align:right;">\n  ', '\n</div>\n', 'Right aligned text')
                          : insertText('[right]', '[/right]', 'Right aligned text')
                      }
                      className="p-1 hover:bg-gray-100 rounded text-gray-700"
                      title="Align Right"
                    >
                      <AlignRight size={13} />
                    </button>
                    <button
                      type="button"
                      onClick={() =>
                        formData.content_type === 'html'
                          ? insertText('<div style="text-align:justify;">\n  ', '\n</div>\n', 'Justified text')
                          : insertText('[justify]', '[/justify]', 'Justified text')
                      }
                      className="p-1 hover:bg-gray-100 rounded text-gray-700"
                      title="Justify Text"
                    >
                      <AlignJustify size={13} />
                    </button>
                  </div>

                  {/* Superscript & Subscript */}
                  <div className="flex items-center bg-white rounded-lg border border-gray-200 p-0.5 shadow-2xs">
                    <button
                      type="button"
                      onClick={() =>
                        formData.content_type === 'html'
                          ? insertText('<sup>', '</sup>', 'sup')
                          : insertText('^(', ')', 'sup')
                      }
                      className="p-1 hover:bg-gray-100 rounded text-gray-700"
                      title="Superscript (e.g. 1st, 2nd)"
                    >
                      <Superscript size={13} />
                    </button>
                    <button
                      type="button"
                      onClick={() =>
                        formData.content_type === 'html'
                          ? insertText('<sub>', '</sub>', 'sub')
                          : insertText('~(', ')~', 'sub')
                      }
                      className="p-1 hover:bg-gray-100 rounded text-gray-700"
                      title="Subscript (e.g. H2O)"
                    >
                      <Subscript size={13} />
                    </button>
                  </div>

                  {/* Lists & Quotes */}
                  <div className="flex items-center bg-white rounded-lg border border-gray-200 p-0.5 shadow-2xs">
                    <button
                      type="button"
                      onClick={() =>
                        formData.content_type === 'html'
                          ? insertText('\n<ul>\n  <li>', '</li>\n  <li>List item 2</li>\n</ul>\n', 'List item')
                          : insertText('\n- ', '\n', 'List item')
                      }
                      className="p-1 hover:bg-gray-100 rounded"
                      title="Bullet List"
                    >
                      <List size={13} />
                    </button>
                    <button
                      type="button"
                      onClick={() =>
                        formData.content_type === 'html'
                          ? insertText('\n<ol>\n  <li>', '</li>\n  <li>Step 2</li>\n</ol>\n', 'Step 1')
                          : insertText('\n1. ', '\n', 'Step 1')
                      }
                      className="p-1 hover:bg-gray-100 rounded"
                      title="Numbered List"
                    >
                      <ListOrdered size={13} />
                    </button>
                    <button
                      type="button"
                      onClick={() =>
                        formData.content_type === 'html'
                          ? insertText(
                              '\n<ul style="list-style:none; padding-left:0;">\n  <li><input type="checkbox" disabled /> ',
                              '</li>\n</ul>\n',
                              'Task / Checklist item'
                            )
                          : insertText('\n- [ ] ', '\n', 'Task / Checklist item')
                      }
                      className="p-1 hover:bg-gray-100 rounded"
                      title="Task Checklist"
                    >
                      <CheckSquare size={13} />
                    </button>
                    <button
                      type="button"
                      onClick={() =>
                        formData.content_type === 'html'
                          ? insertText(
                              '\n<blockquote style="border-left:4px solid #0B2E5C; padding-left:12px; margin:16px 0; color:#4B5563; font-style:italic;">\n  ',
                              '\n</blockquote>\n',
                              'Notable Quote or Takeaway'
                            )
                          : insertText('\n> ', '\n', 'Notable Quote or Takeaway')
                      }
                      className="p-1 hover:bg-gray-100 rounded"
                      title="Blockquote"
                    >
                      <Quote size={13} />
                    </button>
                  </div>

                  {/* Pre-styled Callout Boxes */}
                  <div className="flex items-center bg-white rounded-lg border border-gray-200 p-0.5 shadow-2xs">
                    <button
                      type="button"
                      onClick={() =>
                        formData.content_type === 'html'
                          ? insertText(
                              '\n<div style="background:#F0FDF4; border-left:4px solid #16A34A; padding:16px; border-radius:12px; margin:20px 0;">\n  <strong style="color:#15803D; font-size:15px; display:block; margin-bottom:4px;">💡 Pro Travel Tip:</strong>\n  <span>',
                              '</span>\n</div>\n',
                              'Book morning departures for scenic photography along the route.'
                            )
                          : insertText(
                              '\n> 💡 **Pro Travel Tip:**\n> ',
                              '\n',
                              'Book morning departures for scenic photography along the route.'
                            )
                      }
                      className="px-2 py-0.5 hover:bg-green-50 text-green-700 font-bold rounded flex items-center gap-1 text-[11px]"
                      title="Insert Travel Tip Callout"
                    >
                      <Lightbulb size={12} /> Tip
                    </button>
                    <button
                      type="button"
                      onClick={() =>
                        formData.content_type === 'html'
                          ? insertText(
                              '\n<div style="background:#FEF2F2; border-left:4px solid #DC2626; padding:16px; border-radius:12px; margin:20px 0;">\n  <strong style="color:#B91C1C; font-size:15px; display:block; margin-bottom:4px;">⚠️ Train Advisory:</strong>\n  <span>',
                              '</span>\n</div>\n',
                              'Check live running status on TrainPanda 1 hour before scheduled departure.'
                            )
                          : insertText(
                              '\n> ⚠️ **Train Advisory:**\n> ',
                              '\n',
                              'Check live running status on TrainPanda 1 hour before scheduled departure.'
                            )
                      }
                      className="px-2 py-0.5 hover:bg-red-50 text-red-700 font-bold rounded flex items-center gap-1 text-[11px]"
                      title="Insert Warning / Advisory"
                    >
                      <AlertTriangle size={12} /> Alert
                    </button>
                    <button
                      type="button"
                      onClick={() =>
                        formData.content_type === 'html'
                          ? insertText(
                              '\n<div style="background:#EFF6FF; border-left:4px solid #2563EB; padding:16px; border-radius:12px; margin:20px 0;">\n  <strong style="color:#1D4ED8; font-size:15px; display:block; margin-bottom:4px;">ℹ️ Station Info:</strong>\n  <span>',
                              '</span>\n</div>\n',
                              'Executive lounge is located near Platform 1 exit gate.'
                            )
                          : insertText(
                              '\n> ℹ️ **Station Info:**\n> ',
                              '\n',
                              'Executive lounge is located near Platform 1 exit gate.'
                            )
                      }
                      className="px-2 py-0.5 hover:bg-blue-50 text-blue-700 font-bold rounded flex items-center gap-1 text-[11px]"
                      title="Insert Station Information"
                    >
                      <Info size={12} /> Info
                    </button>
                  </div>

                  {/* Insert Media & Elements */}
                  <div className="flex items-center bg-white rounded-lg border border-gray-200 p-0.5 shadow-2xs">
                    <button
                      type="button"
                      onClick={() => setTableModalOpen(true)}
                      className="px-2 py-0.5 hover:bg-gray-100 text-gray-700 font-bold rounded flex items-center gap-1 text-[11px]"
                      title="Insert Custom Table"
                    >
                      <TableIcon size={12} className="text-tp-violet" /> Table
                    </button>
                    <input
                      type="file"
                      accept="image/*"
                      ref={inlineFileInputRef}
                      onChange={(e) => handleImageUpload(e, 'inline')}
                      className="hidden"
                    />
                    <button
                      type="button"
                      onClick={() => inlineFileInputRef.current?.click()}
                      disabled={uploadingInlineImg}
                      className="p-1 hover:bg-gray-100 text-tp-violet font-bold rounded"
                      title="Upload & Insert Image from Computer"
                    >
                      {uploadingInlineImg ? <Loader2 size={13} className="animate-spin" /> : <Upload size={13} />}
                    </button>
                    <button
                      type="button"
                      onClick={() => setImageModalOpen(true)}
                      className="p-1 hover:bg-gray-100 text-gray-700 font-bold rounded"
                      title="Insert & Align Image by URL"
                    >
                      <ImageIcon size={13} />
                    </button>
                    <button
                      type="button"
                      onMouseDown={(e) => e.preventDefault()}
                      onClick={handleOpenLinkModal}
                      className="p-1 hover:bg-gray-100 text-gray-700 font-bold rounded"
                      title="Insert / Edit Hyperlink"
                    >
                      <LinkIcon size={13} />
                    </button>
                    <button
                      type="button"
                      onClick={() => setVideoModalOpen(true)}
                      className="p-1 hover:bg-red-50 text-red-600 font-bold rounded"
                      title="Embed YouTube Video"
                    >
                      <Youtube size={13} />
                    </button>
                    <button
                      type="button"
                      onClick={() => setEmbedModalOpen(true)}
                      className="p-1 hover:bg-gray-100 text-gray-700 font-bold rounded"
                      title="Embed External Content / Iframe"
                    >
                      <Code2 size={13} />
                    </button>
                    <button
                      type="button"
                      onClick={() =>
                        formData.content_type === 'html'
                          ? insertText('\n<hr style="margin:24px 0; border:0; border-top:1px solid #E2E8F0;" />\n')
                          : insertText('\n---\n')
                      }
                      className="px-2 py-0.5 hover:bg-gray-100 font-bold rounded text-[11px]"
                      title="Horizontal Divider"
                    >
                      HR
                    </button>
                  </div>
                </div>
              </div>

              {/* Editing Canvas & Live Preview Container */}
              <div className="flex flex-col md:flex-row w-full divide-y md:divide-y-0 md:divide-x divide-gray-200 bg-white min-h-[500px]">
                {/* Left: Code / Markdown Editor */}
                {(viewMode === 'write' || viewMode === 'split') && (
                  <div className={`${viewMode === 'split' ? 'w-full md:w-1/2' : 'w-full'} flex flex-col bg-white min-h-[500px]`}>
                    <div className="px-4 py-1.5 bg-gray-50 border-b border-gray-200 flex items-center justify-between text-xs text-gray-500 font-bold shrink-0">
                      <span className="flex items-center gap-1.5 text-gray-700">
                        ✏️ {formData.content_type === 'markdown' ? 'Markdown Editor' : 'HTML Source Editor'}
                      </span>
                      <span className="text-[11px] font-mono text-gray-400">Drag & drop images supported</span>
                    </div>

                    <textarea
                      ref={textareaRef}
                      onSelect={(e) => {
                        lastSelectionRef.current = { start: e.target.selectionStart, end: e.target.selectionEnd };
                      }}
                      onKeyUp={(e) => {
                        lastSelectionRef.current = { start: e.target.selectionStart, end: e.target.selectionEnd };
                      }}
                      onMouseUp={(e) => {
                        lastSelectionRef.current = { start: e.target.selectionStart, end: e.target.selectionEnd };
                      }}
                      onTouchEnd={(e) => {
                        lastSelectionRef.current = { start: e.target.selectionStart, end: e.target.selectionEnd };
                      }}
                      onDrop={handleDropImage}
                      onDragOver={(e) => e.preventDefault()}
                      onPaste={handlePasteSanitizer}
                      placeholder={
                        formData.content_type === 'markdown'
                          ? 'Write your comprehensive travel story here using headings, images, lists, callout boxes, and tables (Drag & drop images supported)...'
                          : 'Write custom HTML code here (<p>, <h2>, <div style="...">, etc.)...'
                      }
                      value={formData.content_type === 'html' ? (formData.content_html ?? '') : (formData.content_markdown ?? '')}
                      onChange={(e) => {
                        const val = e.target.value;
                        const nextForm = {
                          ...formData,
                          [formData.content_type === 'html' ? 'content_html' : 'content_markdown']: val,
                        };
                        setFormData(nextForm);
                        pushHistory(nextForm);
                      }}
                      className={`flex-1 w-full p-5 font-mono text-sm border-0 focus:outline-none focus:ring-0 leading-relaxed resize-none min-h-[460px] ${
                        formData.content_type === 'html' ? 'bg-[#0F172A] text-emerald-400 selection:bg-emerald-800' : 'bg-white text-gray-800'
                      }`}
                    />
                  </div>
                )}

                {/* Right: Live Visual Preview */}
                {(viewMode === 'preview' || viewMode === 'split') && (
                  <div className={`${viewMode === 'split' ? 'w-full md:w-1/2' : 'w-full'} flex flex-col bg-white min-h-[500px]`}>
                    <div className="px-4 py-1.5 bg-gray-50 border-b border-gray-200 flex items-center justify-between text-xs text-gray-500 font-bold shrink-0">
                      <span className="flex items-center gap-1 text-tp-violet">
                        <Eye size={13} /> Live Article Preview
                      </span>
                      <span className="text-[11px] bg-green-50 text-green-700 font-bold px-2 py-0.5 rounded-full">
                        Real-time
                      </span>
                    </div>

                    <div className="flex-1 p-6 prose prose-slate max-w-none min-h-[460px] overflow-y-auto">
                      {/* Hero Image in Preview */}
                      {formData.hero_image && (
                        <div className="rounded-2xl overflow-hidden mb-6 shadow-md">
                          <img src={formData.hero_image} alt="" className="w-full aspect-[16/9] object-cover" />
                        </div>
                      )}

                      {/* Title & Metadata in Preview */}
                      <div className="mb-6">
                        <div className="flex items-center gap-2 mb-2 flex-wrap">
                          <span className="text-[11px] font-extrabold bg-tp-violet-soft text-tp-violet px-2.5 py-0.5 rounded-full uppercase">
                            {formData.category}
                          </span>
                          <span className="text-xs text-gray-400 font-medium">• {formData.read_time || calcReadTime}</span>
                          <span className="text-xs text-gray-400 font-medium">• By {formData.author}</span>
                        </div>
                        <h1 className="text-2xl md:text-3xl font-black text-[#0B2E5C] tracking-tight leading-tight not-prose">
                          {formData.title || 'Untitled Blog Post'}
                        </h1>
                        {formData.excerpt && (
                          <p className="mt-2 text-sm text-gray-600 font-medium leading-relaxed italic">
                            {formData.excerpt}
                          </p>
                        )}
                      </div>

                      <hr className="my-5 border-gray-200" />

                      {/* Content Render */}
                      {formData.content_type === 'html' ? (
                        <div
                          className="article-content space-y-4 text-sm leading-relaxed text-gray-800"
                          dangerouslySetInnerHTML={{ __html: formData.content_html || '<p class="text-gray-400 italic">No HTML content yet...</p>' }}
                        />
                      ) : (
                        <div className="article-content space-y-4 text-sm leading-relaxed text-gray-800">
                          {formData.content_markdown ? (
                            <ReactMarkdown
                              remarkPlugins={[remarkGfm]}
                              rehypePlugins={[rehypeRaw]}
                              components={{
                                h1: (props) => <h1 className="text-3xl font-black text-[#0B2E5C] mt-8 mb-4 border-b border-gray-100 pb-2 leading-tight" {...props} />,
                                h2: (props) => <h2 className="text-2xl font-black text-[#0B2E5C] mt-7 mb-3 border-b border-gray-100 pb-1 leading-snug" {...props} />,
                                h3: (props) => <h3 className="text-xl font-extrabold text-[#0B2E5C] mt-6 mb-2 leading-snug" {...props} />,
                                h4: (props) => <h4 className="text-lg font-bold text-[#0B2E5C] mt-5 mb-2" {...props} />,
                                p: (props) => <p className="text-gray-700 leading-relaxed text-sm mb-4" {...props} />,
                                a: (props) => <a className="text-[#FF5E3A] font-bold underline hover:no-underline cursor-pointer" target="_blank" rel="noopener noreferrer" {...props} />,
                                ul: (props) => <ul className="list-disc pl-5 space-y-1.5 mb-4 text-gray-700 text-sm" {...props} />,
                                ol: (props) => <ol className="list-decimal pl-5 space-y-1.5 mb-4 text-gray-700 text-sm" {...props} />,
                                blockquote: (props) => (
                                  <blockquote className="border-l-4 border-tp-violet bg-tp-violet-soft/40 rounded-r-xl pl-4 py-2.5 my-4 italic text-gray-800 font-medium text-sm" {...props} />
                                ),
                                strong: (props) => <strong className="text-[#0B2E5C] font-extrabold" {...props} />,
                                img: (props) => (
                                  <img className="rounded-xl my-4 max-w-full shadow-sm object-cover border border-gray-100" loading="lazy" alt={props.alt || ''} {...props} />
                                ),
                              }}
                            >
                              {processMarkdownContent(formData.content_markdown)}
                            </ReactMarkdown>
                          ) : (
                            <p className="text-gray-400 italic">Start typing in the editor on the left to see live preview...</p>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* 4. Excerpt & Tags */}
            <div className="bg-white p-5 rounded-2xl soft-shadow border border-gray-100 space-y-4">
              <div>
                <label className="text-xs font-extrabold text-[#0B2E5C] uppercase tracking-wider block mb-1">
                  Article Summary / Short Excerpt (Displayed on Cards & Social Previews)
                </label>
                <Textarea
                  rows={2}
                  placeholder="Write a catchy 1-2 sentence overview of this travel guide..."
                  value={formData.excerpt}
                  onChange={(e) => setFormData({ ...formData, excerpt: e.target.value })}
                  className="text-xs rounded-xl"
                />
              </div>

              <div>
                <label className="text-xs font-extrabold text-[#0B2E5C] uppercase tracking-wider block mb-1">
                  Tags (Comma separated)
                </label>
                <Input
                  placeholder="e.g. Vande Bharat, High Speed Train, Food Guide, Delhi to Bhopal"
                  value={tagInput}
                  onChange={(e) => setTagInput(e.target.value)}
                  className="h-10 text-xs rounded-xl"
                />
              </div>
            </div>

            {/* 5. Google SEO & Social Snippet Preview */}
            <div className="bg-white p-5 rounded-2xl soft-shadow border border-gray-100 space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <h4 className="text-sm font-black text-[#0B2E5C] flex items-center gap-1.5">
                    <Globe size={16} className="text-tp-violet" /> Search Engine Optimization (SEO) & Google Preview
                  </h4>
                  <p className="text-xs text-gray-500">Preview how this story appears in Google search results.</p>
                </div>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={generateMagicSeo}
                  className="text-xs font-bold text-tp-violet border-tp-violet/30 hover:bg-tp-violet-soft rounded-xl h-8 flex items-center gap-1"
                >
                  <Sparkles size={12} /> Auto-Generate SEO
                </Button>
              </div>

              {/* Live Google Search Result Mockup */}
              <div className="p-4 bg-[#F8FAFC] rounded-xl border border-gray-200 max-w-2xl">
                <div className="flex items-center gap-2 mb-1">
                  <div className="w-5 h-5 rounded-full bg-tp-orange/20 text-tp-orange font-bold text-[10px] flex items-center justify-center">
                    TP
                  </div>
                  <div className="text-[12px] text-[#202124] leading-none font-medium">
                    TrainPanda
                    <span className="text-gray-400 text-[11px] ml-1">https://trainpanda.com › stories › {formData.slug || 'your-slug'}</span>
                  </div>
                </div>
                <div className="text-[#1a0dab] hover:underline text-base font-semibold cursor-pointer truncate">
                  {formData.meta_title || formData.title || 'Your Article Title – TrainPanda'}
                </div>
                <div className="text-[#4d5156] text-xs mt-1 line-clamp-2 leading-relaxed">
                  {formData.meta_description || formData.excerpt || 'Explore complete guide, train timings, route, food and travel tips on TrainPanda.'}
                </div>
              </div>

              {/* SEO Inputs */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-1">
                <div>
                  <div className="flex justify-between mb-1">
                    <label className="text-xs font-bold text-gray-600">Meta Title (SEO)</label>
                    <span className={`text-[10px] font-bold ${
                      (formData.meta_title || '').length > 60 ? 'text-amber-600' : 'text-gray-400'
                    }`}>
                      {(formData.meta_title || '').length}/60 chars
                    </span>
                  </div>
                  <Input
                    placeholder="Page title for search engines"
                    value={formData.meta_title}
                    onChange={(e) => setFormData({ ...formData, meta_title: e.target.value })}
                    className="h-10 text-xs rounded-xl"
                  />
                </div>

                <div>
                  <div className="flex justify-between mb-1">
                    <label className="text-xs font-bold text-gray-600">Meta Description (SEO)</label>
                    <span className={`text-[10px] font-bold ${
                      (formData.meta_description || '').length > 160 ? 'text-amber-600' : 'text-gray-400'
                    }`}>
                      {(formData.meta_description || '').length}/160 chars
                    </span>
                  </div>
                  <Input
                    placeholder="Short snippet summary for Google"
                    value={formData.meta_description}
                    onChange={(e) => setFormData({ ...formData, meta_description: e.target.value })}
                    className="h-10 text-xs rounded-xl"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Footer Save Bar */}
          <div className="px-6 py-3 bg-white border-t border-gray-200 flex items-center justify-between shrink-0">
            <Button
              variant="outline"
              onClick={() => setEditorOpen(false)}
              className="rounded-xl text-xs font-bold text-gray-600"
            >
              Cancel
            </Button>

            <Button
              onClick={handleSavePost}
              disabled={saving}
              className="btn-coral text-white font-extrabold text-xs h-9 px-8 rounded-xl flex items-center gap-1.5 shadow-md"
            >
              {saving ? <Loader2 size={15} className="animate-spin" /> : <Save size={15} />}
              {isEditing ? 'Save Changes' : 'Publish Story'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminStories;
